import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { 
  ArrowLeft, Edit, Edit2, FileText, User, Calendar, DollarSign, Truck, Users,
  Settings, Upload, Download, Trash2, Plus, CheckCircle, Clock, PauseCircle,
  XCircle, AlertCircle, Play, Pause, StopCircle, Loader2, Phone, Mail,
  Building, Eye, BarChart3, FileUp, RefreshCw, Send, CreditCard, X,
  Package, Route, MapPin, Weight, FileCheck, Copy, Search, ArrowUpDown, Filter,
  ChevronDown, ChevronUp, MoreHorizontal, TrendingUp, RotateCcw, UserCheck, CalendarDays,
  Shield, ExternalLink, FileMinus2
} from 'lucide-react';
import { Textarea } from '../../../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CreateTransferWizard from '../../../components/CreateTransferWizard';
import TransferCalendar from '../../../components/TransferCalendar';
import HierarchyView from '../../../components/features/HierarchyView';
import BulkStatusChangeModal from '../../../components/BulkStatusChangeModal';
import TransferCSVImportModal from '../../../components/TransferCSVImportModal';
import ContractAnalyticsTab from './ContractAnalyticsTab';
import ContractProfitabilityTab from './ContractProfitabilityTab';
import { CHARGE_POSTES, BEARER_LABELS } from '../../../constants/chargePostes';
import ContractPrefinancingTab from './ContractPrefinancingTab';
import TruckSlotsConfig from './TruckSlotsConfig';
import ComplianceTab from '../../../components/ComplianceTab';
import ContractQuoteHistoryTab from '../../../components/contracts/ContractQuoteHistoryTab';
import { toast } from '../../../hooks/use-toast';
import { useBillingPolicy } from '../../../hooks/useBillingPolicy';
import { cn } from '../../../lib/utils';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../contexts/PermissionsContext';
import InternalNotesTab from '../../../components/notes/InternalNotesTab';
import { formatVehicleType } from '../../../utils/formatters';
import { VEHICLE_TYPES } from '../../../constants/vehicleTypes';
import AdvancedSearchPanel from '../../../components/AdvancedSearchPanel';
import AttendanceCalendar from '../../../components/AttendanceCalendar';
import { logger } from '../../../utils/logger';
import { InvoiceItemRow } from '../../../components/contracts/InvoiceItemRow';
import InvoiceHistoryModal from '../../../components/contracts/InvoiceHistoryModal';
import FneCertifyButton from '../../../components/FneCertifyButton';
import FneCertifyModal from '../../../components/FneCertifyModal';
import FneRefundModal from '../../../components/FneRefundModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts de contrat
const CONTRACT_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock },
  active: { label: 'Actif', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  suspended: { label: 'Suspendu', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: PauseCircle },
  closed: { label: 'Clôturé', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle }
};

const TERMINOLOGY_CONFIG = {
  transfert: { label: 'Transfert', plural: 'Transferts', gender: 'masculine' },
  operation: { label: 'Opération', plural: 'Opérations', gender: 'feminine' },
  course: { label: 'Course', plural: 'Courses', gender: 'feminine' }
};

// Configuration des statuts de transfert (workflow détaillé)
const TRANSFER_STATUS_CONFIG = {
  planifiee: { label: 'Planifié', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: Clock },
  planned: { label: 'Planifié', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: Clock },
  en_cours_de_chargement: { label: 'Chargement', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Package },
  en_transit: { label: 'En transit', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Route },
  en_cours_de_dechargement: { label: 'Déchargement', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: MapPin },
  in_progress: { label: 'En cours', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Play },
  completed: { label: 'Terminé', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
};

const TransferStatusBadge = ({ status }) => {
  const config = TRANSFER_STATUS_CONFIG[status] || TRANSFER_STATUS_CONFIG.planifiee;
  const Icon = config.icon;
  
  return (
    <Badge className={cn('flex items-center gap-1.5 px-2 py-1 text-xs border', config.color)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const ContractStatusBadge = ({ status }) => {
  const config = CONTRACT_STATUS_CONFIG[status] || CONTRACT_STATUS_CONFIG.draft;
  const Icon = config.icon;
  
  return (
    <Badge className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm border', config.color)}>
      <Icon className="w-4 h-4" />
      {config.label}
    </Badge>
  );
};

// Configuration des statuts de facture
const INVOICE_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock },
  validated: { label: 'Validée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
  sent: { label: 'Envoyée', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Send },
  paid: { label: 'Payée', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CreditCard },
  partially_paid: { label: 'Partiel', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: DollarSign },
  overdue: { label: 'En retard', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle }
};

const InvoiceStatusBadge = ({ status }) => {
  const config = INVOICE_STATUS_CONFIG[status] || INVOICE_STATUS_CONFIG.draft;
  const Icon = config.icon;
  
  return (
    <Badge className={cn('flex items-center gap-1.5 px-2 py-1 text-xs border', config.color)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

/* Staff User Search Hook (shared) */
function useStaffSearch() {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/search-users-unified?q=${encodeURIComponent(query)}&include_staff=true&include_clients=false&include_drivers=false&limit=8`, {});
        const data = await res.json();
        setResults(data.users || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const clear = () => { setQuery(''); setResults([]); };
  return { query, setQuery, results, loading, clear };
}

/* Escalation Contacts Editor */
const LEVEL_CONFIG = {
  level1: { label: 'Niveau 1', subtitle: 'Contact initial', dot: 'bg-emerald-500' },
  level2: { label: 'Niveau 2', subtitle: 'Escalade', dot: 'bg-amber-500' },
  level3: { label: 'Niveau 3', subtitle: 'Direction', dot: 'bg-red-500' }
};

function EscalationLevelCard({ level, cfg, data, editing, onUpdate }) {
  const search = useStaffSearch();
  const [mode, setMode] = React.useState('search'); // 'search' | 'manual'
  const empty = !data.name && !data.email && !data.phone;

  const selectUser = (user) => {
    onUpdate(level, {
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      email: user.email || '',
      phone: user.phone || user.phoneNumber || '',
      role: user.role || user.jobTitle || ''
    });
    search.clear();
  };

  const clearLevel = () => {
    onUpdate(level, { name: '', email: '', phone: '', role: '' });
  };

  if (!editing) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
          <span className="text-xs font-medium text-gray-900">{cfg.label}</span>
          <span className="text-xs text-gray-400">{cfg.subtitle}</span>
        </div>
        {empty ? (
          <p className="text-xs text-gray-300 italic py-4 text-center">Non defini</p>
        ) : (
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-gray-900">{data.name}</p>
            {data.role && <p className="text-xs text-gray-400">{data.role}</p>}
            {data.email && <p className="text-xs text-gray-500">{data.email}</p>}
            {data.phone && <p className="text-xs text-gray-500">{data.phone}</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
          <span className="text-xs font-medium text-gray-900">{cfg.label}</span>
        </div>
        {data.name && (
          <button onClick={clearLevel} className="text-xs text-gray-400 hover:text-red-500">Effacer</button>
        )}
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-3 p-0.5 bg-gray-100 rounded-md">
        <button onClick={() => setMode('search')}
          className={`flex-1 text-xs py-1 rounded ${mode === 'search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          Rechercher staff
        </button>
        <button onClick={() => setMode('manual')}
          className={`flex-1 text-xs py-1 rounded ${mode === 'manual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
          Saisie manuelle
        </button>
      </div>

      {mode === 'search' && !data.name && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input className="w-full text-sm border border-gray-200 rounded-md pl-8 pr-3 py-1.5 focus:ring-1 focus:ring-gray-300 focus:outline-none"
            placeholder="Nom ou email..." value={search.query} onChange={e => search.setQuery(e.target.value)} />
          {search.results.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
              {search.results.map(u => (
                <button key={u.id} onClick={() => selectUser(u)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-0">
                  <span className="font-medium text-gray-900">{u.firstName} {u.lastName}</span>
                  <span className="text-gray-400 ml-1">{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'search' && data.name && (
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-gray-900">{data.name}</p>
          {data.role && <p className="text-xs text-gray-400">{data.role}</p>}
          {data.email && <p className="text-xs text-gray-500">{data.email}</p>}
          {data.phone && <p className="text-xs text-gray-500">{data.phone}</p>}
        </div>
      )}

      {mode === 'manual' && (
        <div className="space-y-2">
          <input className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-1 focus:ring-gray-300 focus:outline-none"
            placeholder="Nom complet" value={data.name || ''} onChange={e => onUpdate(level, { ...data, name: e.target.value })} />
          <input className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-1 focus:ring-gray-300 focus:outline-none"
            placeholder="Fonction" value={data.role || ''} onChange={e => onUpdate(level, { ...data, role: e.target.value })} />
          <input className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-1 focus:ring-gray-300 focus:outline-none"
            placeholder="Email" value={data.email || ''} onChange={e => onUpdate(level, { ...data, email: e.target.value })} />
          <input className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:ring-1 focus:ring-gray-300 focus:outline-none"
            placeholder="Telephone" value={data.phone || ''} onChange={e => onUpdate(level, { ...data, phone: e.target.value })} />
        </div>
      )}
    </div>
  );
}

function EscalationContactsEditor({ value, contractId, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState(value || {});
  const [saving, setSaving] = React.useState(false);

  const handleUpdate = (level, data) => {
    setForm(prev => ({ ...prev, [level]: data }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/contracts/${contractId}`, {
        method: 'PATCH', headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'application/json' },
        body: JSON.stringify({ escalationContacts: form })
      });
      onSaved(form);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['level1', 'level2', 'level3'].map(level => (
          <EscalationLevelCard key={level} level={level} cfg={LEVEL_CONFIG[level]}
            data={(editing ? form : value)?.[level] || {}} editing={editing} onUpdate={handleUpdate} />
        ))}
      </div>
      <div className="flex gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}Enregistrer</Button>
            <Button size="sm" variant="outline" onClick={() => { setForm(value || {}); setEditing(false); }}>Annuler</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => { setForm(value || {}); setEditing(true); }}><Edit className="w-3 h-3 mr-1" />Modifier</Button>
        )}
      </div>
    </div>
  );
}

/* Assigned Operators Editor */
function AssignedOperatorsEditor({ value, contractId, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [ops, setOps] = React.useState(value || []);
  const [saving, setSaving] = React.useState(false);
  const [addMode, setAddMode] = React.useState('search'); // 'search' | 'manual'
  const search = useStaffSearch();
  const [manualForm, setManualForm] = React.useState({ name: '', email: '', phone: '' });

  const addFromSearch = (user) => {
    const uid = user.id;
    if (ops.find(o => o.userId === uid)) return;
    setOps(prev => [...prev, {
      userId: uid,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      email: user.email, phone: user.phone || user.phoneNumber || ''
    }]);
    search.clear();
  };

  const addManual = () => {
    if (!manualForm.name.trim()) return;
    setOps(prev => [...prev, {
      userId: `manual_${Date.now()}`,
      name: manualForm.name.trim(),
      email: manualForm.email.trim(),
      phone: manualForm.phone.trim()
    }]);
    setManualForm({ name: '', email: '', phone: '' });
  };

  const removeOp = (userId) => setOps(prev => prev.filter(o => o.userId !== userId));

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/contracts/${contractId}`, {
        method: 'PATCH', headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedOperators: ops })
      });
      onSaved(ops);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  const display = editing ? ops : value || [];

  return (
    <div className="space-y-4">
      {display.length === 0 && !editing && (
        <p className="text-xs text-gray-300 italic py-4 text-center">Aucun operateur assigne</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {display.map((op, i) => (
          <div key={op.userId || i} className="rounded-lg border border-gray-200 bg-white p-4 relative">
            {editing && (
              <button onClick={() => removeOp(op.userId)} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <p className="text-sm font-medium text-gray-900 mb-1">{op.name}</p>
            {op.email && <p className="text-xs text-gray-500">{op.email}</p>}
            {op.phone && <p className="text-xs text-gray-500">{op.phone}</p>}
          </div>
        ))}
      </div>
      {editing && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
          <p className="text-xs font-medium text-gray-600 mb-3">Ajouter un operateur</p>
          {/* Mode toggle */}
          <div className="flex gap-1 mb-3 p-0.5 bg-gray-100 rounded-md w-fit">
            <button onClick={() => setAddMode('search')}
              className={`text-xs px-3 py-1 rounded ${addMode === 'search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              Rechercher staff
            </button>
            <button onClick={() => setAddMode('manual')}
              className={`text-xs px-3 py-1 rounded ${addMode === 'manual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              Saisie manuelle
            </button>
          </div>

          {addMode === 'search' && (
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
              <input className="w-full text-sm border border-gray-200 rounded-md pl-8 pr-3 py-1.5 bg-white focus:ring-1 focus:ring-gray-300 focus:outline-none"
                placeholder="Rechercher par nom ou email..." value={search.query} onChange={e => search.setQuery(e.target.value)} />
              {search.results.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {search.results.map(u => (
                    <button key={u.id} onClick={() => addFromSearch(u)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-0">
                      <span className="font-medium text-gray-900">{u.firstName} {u.lastName}</span>
                      <span className="text-gray-400 ml-1.5">{u.email}</span>
                      {u.role && <span className="text-gray-300 ml-1">({u.role})</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {addMode === 'manual' && (
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-2">
                <input className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white focus:ring-1 focus:ring-gray-300 focus:outline-none"
                  placeholder="Nom complet *" value={manualForm.name} onChange={e => setManualForm(p => ({ ...p, name: e.target.value }))} />
                <div className="flex gap-2">
                  <input className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white focus:ring-1 focus:ring-gray-300 focus:outline-none"
                    placeholder="Email" value={manualForm.email} onChange={e => setManualForm(p => ({ ...p, email: e.target.value }))} />
                  <input className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white focus:ring-1 focus:ring-gray-300 focus:outline-none"
                    placeholder="Telephone" value={manualForm.phone} onChange={e => setManualForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={addManual} disabled={!manualForm.name.trim()}>Ajouter</Button>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}Enregistrer</Button>
            <Button size="sm" variant="outline" onClick={() => { setOps(value || []); setEditing(false); }}>Annuler</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => { setOps(value || []); setEditing(true); }}><Edit className="w-3 h-3 mr-1" />Modifier</Button>
        )}
      </div>
    </div>
  );
}

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [contractNotesCount, setContractNotesCount] = useState(0);
  const [contract, setContract] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [partnerVehicles, setPartnerVehicles] = useState([]);
  const [partnerDrivers, setPartnerDrivers] = useState([]);
  const [showFleetModal, setShowFleetModal] = useState(false);

  // Fleet modal state
  const [fleetItems, setFleetItems] = useState([]);
  const [fleetAmendmentDate, setFleetAmendmentDate] = useState('');
  const [fleetAmendmentReason, setFleetAmendmentReason] = useState('');
  const [savingFleet, setSavingFleet] = useState(false);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSignedDate, setUploadSignedDate] = useState('');
  const [uploadSignatories, setUploadSignatories] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Status change state
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Vehicle/Driver forms
  const [vehicleForm, setVehicleForm] = useState({ vehicleId: '', plate: '', type: '', assignedFrom: '' });
  const [driverForm, setDriverForm] = useState({ driverId: '', name: '', phone: '', assignedFrom: '' });
  const [savingResource, setSavingResource] = useState(false);
  
  // Vehicles/Drivers lists for selection
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [truckDbVehicles, setTruckDbVehicles] = useState([]);
  const [truckDbDrivers, setTruckDbDrivers] = useState([]);
  const [loadingTruckDb, setLoadingTruckDb] = useState(false);
  // Search state for resource modals
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [resourceTab, setResourceTab] = useState('local');
  
  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicePeriodStart, setInvoicePeriodStart] = useState('');
  const [invoicePeriodEnd, setInvoicePeriodEnd] = useState('');
  const [invoiceApplyTVA, setInvoiceApplyTVA] = useState(false);
  const [invoiceShowDetails, setInvoiceShowDetails] = useState(false);
  const [invoiceApplyHiddenTax, setInvoiceApplyHiddenTax] = useState(false);
  const [invoicePaymentTerms, setInvoicePaymentTerms] = useState(30);
  const [invoiceCustomNotes, setInvoiceCustomNotes] = useState('');
  const [invoiceCustomAmount, setInvoiceCustomAmount] = useState('');
  const [invoiceCustomAmountReason, setInvoiceCustomAmountReason] = useState('');
  const [invoiceCustomAmountNote, setInvoiceCustomAmountNote] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const billingPolicy = useBillingPolicy();
  const vatBlocked = !!billingPolicy?.vatBlocked;
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [fneCertifyInvoice, setFneCertifyInvoice] = useState(null);
  const [fneRefundInvoice, setFneRefundInvoice] = useState(null);
  const [showAddExtraModal, setShowAddExtraModal] = useState(false);
  const [extraForm, setExtraForm] = useState({
    designation: '',
    description: '',
    unite_payante: 'Forfait',
    quantite: 1,
    prix_unitaire_ht: 0,
    commentaire: ''
  });
  const [savingExtra, setSavingExtra] = useState(false);
  // PM Spec 17/02/2026 — Frais d'immobilisation (helper structuré)
  const [showAddImmoModal, setShowAddImmoModal] = useState(false);
  const [immoForm, setImmoForm] = useState({
    duration: 0,
    unit: 'heures',
    rate_per_day: 100000,
    franchise: 24,
    amount_override: '', // empty = pas d'override,
    note: '',
  });
  const [savingImmo, setSavingImmo] = useState(false);
  const [invoiceAction, setInvoiceAction] = useState(null); // 'validate', 'send', etc.

  // Invoice status change & payment states
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: '', reference: '', notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [showInvoiceStatusModal, setShowInvoiceStatusModal] = useState(false);
  const [invoiceNewStatus, setInvoiceNewStatus] = useState('');
  const [invoiceStatusReason, setInvoiceStatusReason] = useState('');
  const [changingInvoiceStatus, setChangingInvoiceStatus] = useState(false);

  // ✨ Réouverture/renvoi de factures envoyées (23/04/2026)
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  // Retour brouillon admin (déverrouillage exceptionnel)
  const [showRevertDraftModal, setShowRevertDraftModal] = useState(false);
  const [revertDraftReason, setRevertDraftReason] = useState('');
  const [revertingDraft, setRevertingDraft] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendNotify, setResendNotify] = useState(true);
  const [resending, setResending] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // UX improvements
  const [showGenerationForm, setShowGenerationForm] = useState(false);
  const [inlinePaymentId, setInlinePaymentId] = useState(null);
  const [inlinePaymentForm, setInlinePaymentForm] = useState({ amount: '', method: '' });

  // Billing preview (attendance-based)
  const [billingPreview, setBillingPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [useProrata, setUseProrata] = useState(false);
  // Fix P2 : override tarif par slot AVANT génération (remplacement partiel du tarif par défaut)
  // Format: { "slot-id-1": 3500000, "slot-id-2": 3200000 }
  const [slotRateOverrides, setSlotRateOverrides] = useState({});

  // Contract Runs (Operations) states
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [expandedRunDetails, setExpandedRunDetails] = useState(null);
  const [loadingRunDetails, setLoadingRunDetails] = useState(false);
  
  // Recherche, tri et filtres pour les runs
  const [runsSearchQuery, setRunsSearchQuery] = useState('');
  const [runsStatusFilter, setRunsStatusFilter] = useState('all');
  const [runsSortConfig, setRunsSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [advancedFilteredRuns, setAdvancedFilteredRuns] = useState(null);

  // Pagination (onglet Transferts)
  const [runsCurrentPage, setRunsCurrentPage] = useState(1);
  const [runsPerPage, setRunsPerPage] = useState(25);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRunIds([]);
    setRunsCurrentPage(1);
  }, [advancedFilteredRuns, runsSearchQuery, runsStatusFilter, runsPerPage]);
  
  const [selectedRun, setSelectedRun] = useState(null);
  const [showPODModal, setShowPODModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [validatingPOD, setValidatingPOD] = useState(false);
  const [validatingFuel, setValidatingFuel] = useState(false);
  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [creatingRun, setCreatingRun] = useState(false);
  const [newRunForm, setNewRunForm] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'day',
    plannedStartTime: '06:00',
    plannedEndTime: '18:00',
    plannedVehicleId: '',
    plannedDriverId: '',
    stops: []
  });

  // Parent Operation states (for linking transfers to parent)
  const [parentOperationMode, setParentOperationMode] = useState('existing'); // 'existing' or 'new'
  const [availableParentOperations, setAvailableParentOperations] = useState([]);
  const [loadingParentOperations, setLoadingParentOperations] = useState(false);
  const [selectedParentOperationId, setSelectedParentOperationId] = useState('');
  const [selectedParentOperation, setSelectedParentOperation] = useState(null);

  // Bulk selection states
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showDeleteContractModal, setShowDeleteContractModal] = useState(false);
  const [deletingContract, setDeletingContract] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Bulk association states
  const [showBulkAssociateModal, setShowBulkAssociateModal] = useState(false);
  const [bulkAssociating, setBulkAssociating] = useState(false);
  const [associateTargetOperationId, setAssociateTargetOperationId] = useState('');
  const [associateSearchQuery, setAssociateSearchQuery] = useState('');
  const [allParentOperations, setAllParentOperations] = useState([]);
  const [loadingAllParentOps, setLoadingAllParentOps] = useState(false);

  // Bulk assign resources (vehicle/driver)
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignVehicle, setBulkAssignVehicle] = useState(null); // { id, registration, label }
  const [bulkAssignDriver, setBulkAssignDriver] = useState(null);   // { id, name, phone }
  const [bulkVehicleQ, setBulkVehicleQ] = useState('');
  const [bulkDriverQ, setBulkDriverQ] = useState('');
  const [bulkVehicleOpts, setBulkVehicleOpts] = useState([]);
  const [bulkDriverOpts, setBulkDriverOpts] = useState([]);
  const [loadingBulkVehicles, setLoadingBulkVehicles] = useState(false);
  const [loadingBulkDrivers, setLoadingBulkDrivers] = useState(false);

  // CSV Import modal state
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);

  const fetchContract = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contracts/${id}`);
      setContract(response.data);
      // Initialize fleet items from contract data
      const fleet = response.data?.billingRules?.fleetItems || [];
      setFleetItems(fleet.length > 0 ? fleet : [
        { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
      ]);
      
      // Fetch KPI
      try {
        const kpiResponse = await axios.get(`${API}/contracts/${id}/kpi`);
        setKpi(kpiResponse.data);
      } catch (e) {
        logger.debug('KPI not available yet');
      }
      
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le contrat",
        variant: "destructive"
      });
      navigate('/admin/contracts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchResources = useCallback(async () => {
    // Fetch local vehicles (non-blocking)
    try {
      const vehiclesRes = await axios.get(`${API}/vehicles?limit=200`);
      setAvailableVehicles(vehiclesRes.data.items || vehiclesRes.data || []);
    } catch { setAvailableVehicles([]); }
    
    // Fetch local drivers (non-blocking)
    try {
      const driversRes = await axios.get(`${API}/drivers?limit=200`);
      const d = driversRes.data;
      setAvailableDrivers(Array.isArray(d) ? d : d.items || []);
    } catch { setAvailableDrivers([]); }

    // Fetch partner-linked vehicles/drivers if contract has a partner
    const partnerId = contract?.transportPartnerId;
    if (partnerId) {
      try {
        const partnerRes = await axios.get(`${API}/transport-partners/${partnerId}`);
        setPartnerVehicles(partnerRes.data?.linked_vehicles || []);
        setPartnerDrivers(partnerRes.data?.linked_drivers || []);
      } catch { setPartnerVehicles([]); setPartnerDrivers([]); }
    }
    
    // Fetch Truck DB resources (non-blocking)
    setLoadingTruckDb(true);
    try {
      const [tdbVehicles, tdbDrivers] = await Promise.all([
        axios.get(`${API}/external/vehicles?limit=200`).catch(() => ({ data: { vehicles: [] } })),
        axios.get(`${API}/external/drivers?limit=200`).catch(() => ({ data: { drivers: [] } }))
      ]);
      setTruckDbVehicles(tdbVehicles.data.vehicles || []);
      setTruckDbDrivers(tdbDrivers.data.drivers || []);
    } catch {
      logger.debug('Truck DB non disponible');
    } finally {
      setLoadingTruckDb(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (!id) return;
    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${API}/contract-invoices/by-contract/${id}`);
      setInvoices(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  }, [id]);

  const fetchRuns = useCallback(async () => {
    if (!id) return;
    setLoadingRuns(true);
    try {
      const response = await axios.get(`${API}/contract-runs?contract_id=${id}&limit=100`);
      setRuns(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setLoadingRuns(false);
    }
  }, [id]);

  // Fetch available parent operations for the contract's client
  const fetchParentOperations = useCallback(async () => {
    if (!contract?.clientId) return;
    setLoadingParentOperations(true);
    try {
      // Get operations for this client that could be parent operations
      const response = await axios.get(`${API}/transport-requests?client_id=${contract.clientId}&limit=50`);
      const operations = response.data.items || response.data || [];
      // Filter out operations that might not be suitable as parents
      const validOperations = operations.filter(op => 
        ['demande_enregistree', 'prete_au_chargement', 'chargement', 'en_route', 'livree'].includes(op.status)
      );
      setAvailableParentOperations(validOperations);
    } catch (error) {
      console.error('Error fetching parent operations:', error);
    } finally {
      setLoadingParentOperations(false);
    }
  }, [contract?.clientId]);

  // Fetch all parent operations for bulk association modal
  const fetchAllParentOperations = useCallback(async () => {
    setLoadingAllParentOps(true);
    try {
      const response = await axios.get(`${API}/transport-requests?limit=100&is_parent=true`);
      const operations = response.data.items || response.data || [];
      setAllParentOperations(operations);
    } catch (error) {
      console.error('Error fetching parent operations:', error);
    } finally {
      setLoadingAllParentOps(false);
    }
  }, []);

  useEffect(() => {
    fetchContract();
    fetchResources();
  }, [fetchContract, fetchResources]);

  useEffect(() => {
    if (contract) {
      fetchInvoices();
      fetchRuns();
      fetchParentOperations();
      // Initialize invoice options from contract
      setInvoiceApplyTVA(contract.billingRules?.applyTVA || false);
      setInvoiceShowDetails(contract.invoiceDisplayOptions?.showDetailedLines || false);
      setInvoiceApplyHiddenTax(contract.hiddenTaxEnabled || false);
    }
  }, [contract, fetchInvoices, fetchRuns, fetchParentOperations]);

  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      toast({ title: "Erreur", description: "Veuillez sélectionner un fichier", variant: "destructive" });
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadSignedDate) formData.append('signed_date', uploadSignedDate);
      if (uploadSignatories) formData.append('signatories', uploadSignatories);
      
      await axios.post(`${API}/contracts/${id}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({ title: "Succès", description: "Document uploadé avec succès" });
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadSignedDate('');
      setUploadSignatories('');
      fetchContract();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'upload",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}/document`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract?.signedDocument?.fileName || 'contrat.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de télécharger le document", variant: "destructive" });
    }
  };

  const handleDeleteDocument = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API}/contracts/${id}/document`);
      toast({ title: "Succès", description: "Document supprimé" });
      fetchContract();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    
    setChangingStatus(true);
    try {
      // Restauration (clôturé → actif ou annulé → brouillon)
      if (newStatus === 'restore') {
        await axios.post(`${API}/contracts/${id}/restore`, null, {
          params: { reason: statusReason || undefined }
        });
        toast({ title: "Succès", description: "Contrat restauré avec succès" });
      } else {
        const endpoint = {
          'active': contract.status === 'suspended' ? 'reactivate' : 'activate',
          'suspended': 'suspend',
          'closed': 'close'
        }[newStatus] || 'activate';
        
        await axios.post(`${API}/contracts/${id}/${endpoint}`, null, {
          params: { reason: statusReason || undefined }
        });
        toast({ title: "Succès", description: "Statut mis à jour" });
      }
      
      setShowStatusModal(false);
      setNewStatus('');
      setStatusReason('');
      fetchContract();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors du changement de statut",
        variant: "destructive"
      });
    } finally {
      setChangingStatus(false);
    }
  };

  const handleAddVehicle = async () => {
    if (!vehicleForm.plate || !vehicleForm.type || !vehicleForm.assignedFrom) {
      toast({ title: "Erreur", description: "Veuillez remplir tous les champs obligatoires", variant: "destructive" });
      return;
    }
    
    setSavingResource(true);
    try {
      await axios.patch(`${API}/contracts/${id}/vehicles`, {
        vehicleId: vehicleForm.vehicleId || vehicleForm.plate,
        plate: vehicleForm.plate,
        type: vehicleForm.type,
        assignedFrom: vehicleForm.assignedFrom
      });

      // Also link vehicle to the contract's partner (enrichment)
      const partnerId = contract?.transportPartnerId;
      if (partnerId && vehicleForm.vehicleId) {
        try {
          await axios.post(`${API}/vehicles/${vehicleForm.vehicleId}/assign-partner`, {
            partnerId,
            partnerName: contract?.transportPartnerName
          });
        } catch { /* Vehicle might already be linked — ignore */ }
      }
      
      toast({ title: "Succes", description: "Vehicule ajoute" });
      setShowVehicleModal(false);
      setVehicleForm({ vehicleId: '', plate: '', type: '', assignedFrom: '' });
      fetchContract();
      
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de l'ajout", variant: "destructive" });
    } finally {
      setSavingResource(false);
    }
  };

  const handleRemoveVehicle = async (vehicleId) => {
    if (!window.confirm('Retirer ce véhicule du contrat ?')) return;
    
    try {
      await axios.delete(`${API}/contracts/${id}/vehicles/${vehicleId}`);
      toast({ title: "Succès", description: "Véhicule retiré" });
      fetchContract();
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors du retrait", variant: "destructive" });
    }
  };

  const handleAddDriver = async () => {
    if (!driverForm.name || !driverForm.phone || !driverForm.assignedFrom) {
      toast({ title: "Erreur", description: "Veuillez remplir tous les champs obligatoires", variant: "destructive" });
      return;
    }
    
    setSavingResource(true);
    try {
      await axios.patch(`${API}/contracts/${id}/drivers`, {
        driverId: driverForm.driverId || driverForm.phone,
        name: driverForm.name,
        phone: driverForm.phone,
        assignedFrom: driverForm.assignedFrom
      });

      // Also link driver to the contract's partner (enrichment)
      const partnerId = contract?.transportPartnerId;
      if (partnerId && driverForm.driverId) {
        try {
          await axios.post(`${API}/drivers/${driverForm.driverId}/assign-partner`, {
            partnerId,
            partnerName: contract?.transportPartnerName
          });
        } catch { /* Driver might already be linked — ignore */ }
      }
      
      toast({ title: "Succes", description: "Chauffeur ajoute" });
      setShowDriverModal(false);
      setDriverForm({ driverId: '', name: '', phone: '', assignedFrom: '' });
      fetchContract();
      
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de l'ajout", variant: "destructive" });
    } finally {
      setSavingResource(false);
    }
  };

  const handleRemoveDriver = async (driverId) => {
    if (!window.confirm('Retirer ce chauffeur du contrat ?')) return;
    
    try {
      await axios.delete(`${API}/contracts/${id}/drivers/${driverId}`);
      toast({ title: "Succès", description: "Chauffeur retiré" });
      fetchContract();
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors du retrait", variant: "destructive" });
    }
  };

  // ============================================================
  // FLEET HANDLERS
  // ============================================================

  const fleetTotal = fleetItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.monthlyRatePerTruck || 0), 0
  );
  const fleetTotalTrucks = fleetItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

  const addFleetItem = () => {
    setFleetItems(prev => [
      ...prev,
      { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
    ]);
  };

  const removeFleetItem = (index) => {
    setFleetItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateFleetItem = (index, field, value) => {
    setFleetItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (field === 'vehicleType') {
        const found = VEHICLE_TYPES.find(v => v.value === value);
        return { ...item, vehicleType: value, vehicleTypeLabel: found ? found.label : value, vehicleTypeIcon: found ? (found.icon || '🚛') : '🚛' };
      }
      return { ...item, [field]: value };
    }));
  };

  const handleSaveFleet = async () => {
    const invalid = fleetItems.some(i => !i.vehicleType || !i.quantity || !i.monthlyRatePerTruck);
    if (fleetItems.length === 0 || invalid) {
      toast({ title: "Erreur", description: "Veuillez renseigner tous les types de camion avec quantité et tarif", variant: "destructive" });
      return;
    }
    if (!fleetAmendmentDate) {
      toast({ title: "Erreur", description: "Veuillez renseigner la date d'effet de l'avenant", variant: "destructive" });
      return;
    }
    if (!fleetAmendmentReason.trim()) {
      toast({ title: "Erreur", description: "Le motif de l'avenant est obligatoire", variant: "destructive" });
      return;
    }

    setSavingFleet(true);
    try {
      await axios.patch(`${API}/contracts/${id}/fleet`, {
        effectiveDate: fleetAmendmentDate,
        reason: fleetAmendmentReason.trim(),
        newFleetItems: fleetItems.map(i => ({
          ...i,
          quantity: parseInt(i.quantity) || 1,
          monthlyRatePerTruck: parseFloat(i.monthlyRatePerTruck) || 0
        }))
      });
      toast({ title: "Succès", description: "Flotte mise à jour — avenant enregistré" });
      setShowFleetModal(false);
      setFleetAmendmentReason('');
      fetchContract();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de la mise à jour", variant: "destructive" });
    } finally {
      setSavingFleet(false);
    }
  };

  // ============================================================
  // INVOICE HANDLERS
  // ============================================================

  const handleGenerateInvoice = async () => {
    if (!invoicePeriodStart || !invoicePeriodEnd) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une période", variant: "destructive" });
      return;
    }
    
    setGeneratingInvoice(true);
    try {
      const response = await axios.post(`${API}/contract-invoices/generate/${id}`, {
        periodStart: invoicePeriodStart,
        periodEnd: invoicePeriodEnd,
        showDetailedLines: invoiceShowDetails,
        applyTVA: invoiceApplyTVA,
        applyHiddenTax: invoiceApplyHiddenTax,
        terminology: contract.terminology,
        paymentTermsDays: parseInt(invoicePaymentTerms) || 30,
        customNotes: invoiceCustomNotes || null,
        customAmount: invoiceCustomAmount ? parseFloat(invoiceCustomAmount) : null,
        customAmountReason: invoiceCustomAmount && invoiceCustomAmountReason ? invoiceCustomAmountReason : null,
        customAmountNote: invoiceCustomAmount && invoiceCustomAmountNote ? invoiceCustomAmountNote : null,
        useProrata: useProrata,
        slotRateOverrides: Object.keys(slotRateOverrides).length > 0 ? slotRateOverrides : null,
      });
      
      toast({ title: "Succès", description: "Facture générée avec succès" });
      setInvoicePeriodStart('');
      setInvoicePeriodEnd('');
      setInvoiceCustomNotes('');
      setInvoiceCustomAmount('');
      setInvoiceCustomAmountReason('');
      setInvoiceCustomAmountNote('');
      setUseProrata(false);
      setSlotRateOverrides({});
      fetchInvoices();
      
      // Open the newly created invoice
      if (response.data.invoice) {
        setSelectedInvoice(response.data.invoice);
        setShowInvoiceDetailModal(true);
      }
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la génération",
        variant: "destructive"
      });
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Fetch billing preview based on attendance
  const fetchBillingPreview = async (startDate, endDate) => {
    if (!startDate || !endDate || !contract?.billingRules?.billingMode) return;
    if (contract.billingRules.billingMode !== 'fleet_location') {
      setBillingPreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/contract-invoices/preview/${id}`, {
        periodStart: startDate,
        periodEnd: endDate
      });
      setBillingPreview(res.data);
    } catch (err) {
      console.error('Preview error:', err);
      setBillingPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Auto-fetch preview when dates change
  useEffect(() => {
    if (invoicePeriodStart && invoicePeriodEnd && showGenerationForm) {
      const timer = setTimeout(() => fetchBillingPreview(invoicePeriodStart, invoicePeriodEnd), 300);
      return () => clearTimeout(timer);
    } else {
      setBillingPreview(null);
    }
  }, [invoicePeriodStart, invoicePeriodEnd, showGenerationForm]);

  const handleValidateInvoice = async (invoiceId) => {
    setInvoiceAction('validate');
    try {
      const response = await axios.post(`${API}/contract-invoices/${invoiceId}/validate`);
      toast({ title: "Succès", description: "Facture validée" });
      fetchInvoices();
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(response.data.invoice);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la validation",
        variant: "destructive"
      });
    } finally {
      setInvoiceAction(null);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setInvoiceAction('send');
    try {
      const response = await axios.post(`${API}/contract-invoices/${invoiceId}/send`);
      toast({ title: "Succès", description: "Facture envoyée au client" });
      fetchInvoices();
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(response.data.invoice);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'envoi",
        variant: "destructive"
      });
    } finally {
      setInvoiceAction(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Supprimer cette facture ? Cette action est irréversible.')) return;
    
    try {
      await axios.delete(`${API}/contract-invoices/${invoiceId}`);
      toast({ title: "Succès", description: "Facture supprimée" });
      fetchInvoices();
      if (selectedInvoice?.id === invoiceId) {
        setShowInvoiceDetailModal(false);
        setSelectedInvoice(null);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Seules les factures brouillon peuvent être supprimées",
        variant: "destructive"
      });
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId, invoiceNumber, version = null) => {
    try {
      // Cache-buster : évite tout cache axios/navigateur/proxy
      const bust = Date.now();
      const response = await axios.get(
        `${API}/contract-invoices/${invoiceId}/download?t=${bust}`,
        {
          responseType: 'blob',
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Cache-Control': 'no-cache',
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Nom de fichier versionné si version > 1 → navigateur ne réutilise pas
      // l'ancien fichier déjà présent dans les téléchargements
      const suffix = version && version > 1 ? `_V${version}` : '';
      link.setAttribute('download', `Facture_${invoiceNumber || invoiceId}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({ title: "Succès", description: "Facture téléchargée" });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger la facture",
        variant: "destructive"
      });
    }
  };

  const handleAddExtra = async () => {
    if (!selectedInvoice || !extraForm.designation || !extraForm.prix_unitaire_ht) {
      toast({ title: "Erreur", description: "Veuillez remplir les champs obligatoires", variant: "destructive" });
      return;
    }
    
    setSavingExtra(true);
    try {
      const response = await axios.post(`${API}/contract-invoices/${selectedInvoice.id}/extras`, {
        designation: extraForm.designation,
        description: extraForm.description,
        unite_payante: extraForm.unite_payante,
        quantite: parseFloat(extraForm.quantite) || 1,
        prix_unitaire_ht: parseFloat(extraForm.prix_unitaire_ht),
        commentaire: extraForm.commentaire
      });
      
      toast({ title: "Succès", description: "Extra ajouté" });
      setShowAddExtraModal(false);
      setExtraForm({
        designation: '',
        description: '',
        unite_payante: 'Forfait',
        quantite: 1,
        prix_unitaire_ht: 0,
        commentaire: ''
      });
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'ajout",
        variant: "destructive"
      });
    } finally {
      setSavingExtra(false);
    }
  };

  const handleRemoveExtra = async (extraId) => {
    if (!selectedInvoice) return;
    if (!window.confirm('Supprimer cet extra ?')) return;
    
    try {
      const response = await axios.delete(`${API}/contract-invoices/${selectedInvoice.id}/extras/${extraId}`);
      toast({ title: "Succès", description: "Extra supprimé" });
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    }
  };

  // PM Spec 17/02/2026 — Helper pour ajouter des frais d'immobilisation structurés
  const handleAddImmobilization = async () => {
    if (!selectedInvoice) return;
    if (!immoForm.duration || immoForm.duration <= 0) {
      toast({ title: 'Erreur', description: 'La durée doit être supérieure à 0', variant: 'destructive' });
      return;
    }
    if (!immoForm.rate_per_day || immoForm.rate_per_day <= 0) {
      toast({ title: 'Erreur', description: 'Le tarif/jour doit être supérieur à 0', variant: 'destructive' });
      return;
    }
    setSavingImmo(true);
    try {
      const payload = {
        duration: parseFloat(immoForm.duration),
        unit: immoForm.unit,
        rate_per_day: parseFloat(immoForm.rate_per_day),
        franchise: parseFloat(immoForm.franchise) || 0,
        note: immoForm.note || null,
      };
      if (immoForm.amount_override !== '' && immoForm.amount_override !== null) {
        payload.amount_override = parseFloat(immoForm.amount_override);
      }
      const response = await axios.post(
        `${API}/contract-invoices/${selectedInvoice.id}/extras/immobilization`,
        payload
      );
      toast({ title: 'Succès', description: 'Frais d\'immobilisation ajoutés' });
      setShowAddImmoModal(false);
      setImmoForm({ duration: 0, unit: 'heures', rate_per_day: 100000, franchise: 24, amount_override: '', note: '' });
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible d\'ajouter les frais d\'immobilisation',
        variant: 'destructive',
      });
    } finally {
      setSavingImmo(false);
    }
  };

  // Aperçu calcul (pour affichage temps réel dans la modale)
  const immoPreview = (() => {
    const dur = parseFloat(immoForm.duration) || 0;
    const fr = parseFloat(immoForm.franchise) || 0;
    const rate = parseFloat(immoForm.rate_per_day) || 0;
    const billable = Math.max(0, dur - fr);
    const perUnit = immoForm.unit === 'heures' ? rate / 24 : rate;
    const auto = Math.round(billable * perUnit);
    const override = immoForm.amount_override !== '' ? parseFloat(immoForm.amount_override) : null;
    return { billable, auto, final: override !== null && !Number.isNaN(override) ? override : auto, overridden: override !== null && !Number.isNaN(override) && override !== auto };
  })();


  // ============================================================
  // INVOICE ITEMS — Lignes principales (édition inline en DRAFT)
  // ============================================================

  const handleUpdateInvoiceItem = async (itemId, patch) => {
    if (!selectedInvoice) return;
    try {
      const response = await axios.patch(
        `${API}/contract-invoices/${selectedInvoice.id}/items/${itemId}`,
        patch
      );
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de modifier la ligne',
        variant: 'destructive',
      });
    }
  };

  // Mise à jour des paramètres de taxation (TVA / taxe cachée 2%) sur une facture DRAFT
  const handleUpdateInvoiceTax = async (patch) => {
    if (!selectedInvoice) return;
    try {
      const response = await axios.patch(
        `${API}/contract-invoices/${selectedInvoice.id}`,
        patch
      );
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
      toast({ title: 'Paramètres mis à jour', description: 'Totaux recalculés.' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de mettre à jour la taxation',
        variant: 'destructive',
      });
    }
  };

  const [autoEditItemId, setAutoEditItemId] = useState(null);

  const handleAddInvoiceItem = async () => {
    if (!selectedInvoice) return;
    try {
      // Crée une ligne vide avec valeurs par défaut, puis auto-ouvre en mode édition inline.
      const response = await axios.post(`${API}/contract-invoices/${selectedInvoice.id}/items`, {
        designation: 'Nouvelle ligne',
        unite_payante: 'Forfait',
        quantite: 1,
        prix_unitaire_ht: 0,
      });
      const invoice = response.data.invoice;
      setSelectedInvoice(invoice);
      fetchInvoices();
      // L'API retourne les items dans l'ordre d'insertion — on auto-édite le dernier
      const items = invoice?.invoiceItems || [];
      const newItem = items[items.length - 1];
      if (newItem) setAutoEditItemId(newItem.id);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Ajout impossible',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveInvoiceItem = async (itemId) => {
    if (!selectedInvoice) return;
    try {
      const response = await axios.delete(`${API}/contract-invoices/${selectedInvoice.id}/items/${itemId}`);
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Suppression impossible',
        variant: 'destructive',
      });
    }
  };

  const openInvoiceDetail = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailModal(true);
    // Fetch full invoice detail (list endpoint returns shallow data without invoiceItems)
    try {
      const response = await axios.get(`${API}/contract-invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
    } catch (err) {
      logger.debug('Failed to fetch invoice detail', err);
    }
  };

  // ============================================================
  // INVOICE STATUS CHANGE & PAYMENT HANDLERS
  // ============================================================

  const handleRecordPayment = async () => {
    if (!selectedInvoice || !paymentForm.amount || !paymentForm.method) {
      toast({ title: "Erreur", description: "Montant et méthode de paiement requis", variant: "destructive" });
      return;
    }

    setSavingPayment(true);
    try {
      const response = await axios.post(`${API}/contract-invoices/${selectedInvoice.id}/payment`, {
        amount: parseFloat(paymentForm.amount),
        method: paymentForm.method,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      });

      toast({ title: "Succès", description: "Paiement enregistré" });
      setPaymentForm({ amount: '', method: '', reference: '', notes: '' });
      setShowPaymentForm(false);
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'enregistrement",
        variant: "destructive"
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const handleChangeInvoiceStatus = async () => {
    if (!selectedInvoice || !invoiceNewStatus) return;

    setChangingInvoiceStatus(true);
    try {
      const response = await axios.post(`${API}/contract-invoices/${selectedInvoice.id}/change-status`, {
        newStatus: invoiceNewStatus,
        reason: invoiceStatusReason || null
      });

      toast({ title: "Succès", description: `Statut mis à jour: ${INVOICE_STATUS_CONFIG[invoiceNewStatus]?.label || invoiceNewStatus}` });
      setShowInvoiceStatusModal(false);
      setInvoiceNewStatus('');
      setInvoiceStatusReason('');
      setSelectedInvoice(response.data.invoice);
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Transition de statut non autorisée",
        variant: "destructive"
      });
    } finally {
      setChangingInvoiceStatus(false);
    }
  };

  const getInvoiceStatusTransitions = (status) => {
    const transitions = {
      draft: [{ value: 'validated', label: 'Valider', icon: CheckCircle, color: 'bg-blue-600 hover:bg-blue-700' }],
      validated: [
        { value: 'sent', label: 'Marquer envoyée', icon: Send, color: 'bg-purple-600 hover:bg-purple-700' },
        { value: 'draft', label: 'Repasser brouillon', icon: Clock, color: 'bg-gray-600 hover:bg-gray-700' }
      ],
      sent: [
        { value: 'paid', label: 'Marquer payée', icon: CreditCard, color: 'bg-emerald-600 hover:bg-emerald-700' },
        { value: 'partially_paid', label: 'Paiement partiel', icon: DollarSign, color: 'bg-amber-600 hover:bg-amber-700' },
        { value: 'overdue', label: 'Marquer en retard', icon: AlertCircle, color: 'bg-red-600 hover:bg-red-700' }
      ],
      partially_paid: [
        { value: 'paid', label: 'Marquer payée', icon: CreditCard, color: 'bg-emerald-600 hover:bg-emerald-700' },
        { value: 'overdue', label: 'Marquer en retard', icon: AlertCircle, color: 'bg-red-600 hover:bg-red-700' }
      ],
      overdue: [
        { value: 'paid', label: 'Marquer payée', icon: CreditCard, color: 'bg-emerald-600 hover:bg-emerald-700' },
        { value: 'partially_paid', label: 'Paiement partiel', icon: DollarSign, color: 'bg-amber-600 hover:bg-amber-700' },
        { value: 'sent', label: 'Remettre envoyée', icon: Send, color: 'bg-purple-600 hover:bg-purple-700' }
      ]
    };
    return transitions[status] || [];
  };

  const handleInlinePayment = async (invoice) => {
    if (!inlinePaymentForm.amount || !inlinePaymentForm.method) {
      toast({ title: "Erreur", description: "Montant et méthode requis", variant: "destructive" });
      return;
    }
    setSavingPayment(true);
    try {
      await axios.post(`${API}/contract-invoices/${invoice.id}/payment`, {
        amount: parseFloat(inlinePaymentForm.amount),
        method: inlinePaymentForm.method,
        reference: null,
        notes: null
      });
      toast({ title: "Paiement enregistré" });
      setInlinePaymentId(null);
      setInlinePaymentForm({ amount: '', method: '' });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur", variant: "destructive" });
    } finally {
      setSavingPayment(false);
    }
  };

  const handleQuickStatusChange = async (invoiceId, newStatus) => {
    try {
      await axios.post(`${API}/contract-invoices/${invoiceId}/change-status`, { newStatus });
      toast({ title: "Statut mis à jour" });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Transition non autorisée", variant: "destructive" });
    }
  };

  // ✨ Rouvrir une facture envoyée pour édition (Mode 1 — aucun paiement reçu)
  const handleReopenInvoice = async () => {
    if (!selectedInvoice) return;
    if (!reopenReason || reopenReason.trim().length < 10) {
      toast({ title: "Raison requise", description: "Merci de préciser la raison (min. 10 caractères)", variant: "destructive" });
      return;
    }
    setReopening(true);
    try {
      const res = await axios.post(
        `${API}/contract-invoices/${selectedInvoice.id}/reopen`,
        { reason: reopenReason.trim() }
      );
      toast({ title: "Facture rouverte", description: res.data.message });
      setSelectedInvoice(res.data.invoice);
      setShowReopenModal(false);
      setReopenReason('');
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Réouverture impossible",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive",
      });
    } finally {
      setReopening(false);
    }
  };

  // 🔓 Retour brouillon (Admin only) — déverrouillage exceptionnel pour factures envoyées / payées / etc.
  const handleRevertToDraft = async () => {
    if (!selectedInvoice) return;
    const reason = (revertDraftReason || '').trim();
    if (reason.length < 20) {
      toast({ title: 'Motif requis', description: 'Merci de préciser un motif d\'au moins 20 caractères (audit).', variant: 'destructive' });
      return;
    }
    setRevertingDraft(true);
    try {
      const res = await axios.post(
        `${API}/contract-invoices/${selectedInvoice.id}/revert-to-draft`,
        { reason }
      );
      toast({ title: 'Facture repassée en brouillon', description: res.data.message });
      setSelectedInvoice(res.data.invoice);
      setShowRevertDraftModal(false);
      setRevertDraftReason('');
      fetchInvoices();
    } catch (error) {
      toast({
        title: 'Retour en brouillon impossible',
        description: error.response?.data?.detail || 'Erreur',
        variant: 'destructive',
      });
    } finally {
      setRevertingDraft(false);
    }
  };

  // ✨ Renvoyer une facture modifiée au client (Version N+)
  const handleResendInvoice = async () => {
    if (!selectedInvoice) return;
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/contract-invoices/${selectedInvoice.id}/resend`,
        { notify_client: resendNotify }
      );
      toast({ title: "Facture renvoyée", description: res.data.message });
      setSelectedInvoice(res.data.invoice);
      setShowResendModal(false);
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors du renvoi",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  // Billing KPI calculations
  const billingKPIs = React.useMemo(() => {
    const total = invoices.reduce((s, i) => s + (i.montantTotalTTC || 0), 0);
    const paid = invoices.reduce((s, i) => s + (i.amountPaid || 0), 0);
    const unpaid = invoices.reduce((s, i) => s + (i.amountDue || 0), 0);
    const overdueCount = invoices.filter(i => i.status === 'overdue').length;
    return { total, paid, unpaid, overdueCount };
  }, [invoices]);

  // ============================================================
  // CONTRACT RUNS (OPERATIONS) HANDLERS
  // ============================================================

  const handleCreateRun = async () => {
    if (!newRunForm.date) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une date", variant: "destructive" });
      return;
    }
    
    // Validate parent operation selection
    if (parentOperationMode === 'existing' && !selectedParentOperationId) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une opération principale", variant: "destructive" });
      return;
    }
    
    setCreatingRun(true);
    try {
      if (parentOperationMode === 'existing' && selectedParentOperationId) {
        // Create as child of existing parent operation
        await axios.post(`${API}/operations/${selectedParentOperationId}/children`, {
          date: newRunForm.date,
          shift: newRunForm.shift,
          plannedStartTime: newRunForm.plannedStartTime,
          plannedEndTime: newRunForm.plannedEndTime,
          useParentVehicle: !newRunForm.plannedVehicleId,
          useParentDriver: !newRunForm.plannedDriverId,
          overrideVehicleId: newRunForm.plannedVehicleId || null,
          overrideDriverId: newRunForm.plannedDriverId || null,
          contractId: id
        });
      } else {
        // Create standalone (old behavior - will auto-create transport_request)
        await axios.post(`${API}/contract-runs`, {
          contractId: id,
          parentOperationId: null,  // Will be auto-created,
          date: newRunForm.date,
          shift: newRunForm.shift,
          plannedStartTime: newRunForm.plannedStartTime,
          plannedEndTime: newRunForm.plannedEndTime,
          plannedVehicleId: newRunForm.plannedVehicleId || null,
          plannedDriverId: newRunForm.plannedDriverId || null,
          stops: newRunForm.stops.length > 0 ? newRunForm.stops : [{
            order: 1,
            address: contract.operationalDetails?.defaultDepartureAddress || "À définir",
            stopType: "DELIVERY",
            isBillable: true
          }]
        });
      }
      
      toast({ title: "Succès", description: `${terminologySingular} ${isMasculine ? 'créé' : 'créée'}` });
      setShowCreateRunModal(false);
      setNewRunForm({
        date: new Date().toISOString().split('T')[0],
        shift: 'day',
        plannedStartTime: '06:00',
        plannedEndTime: '18:00',
        plannedVehicleId: '',
        plannedDriverId: '',
        stops: []
      });
      setSelectedParentOperationId('');
      setSelectedParentOperation(null);
      setParentOperationMode('existing');
      fetchRuns();
      // Refresh KPI
      try {
        const kpiResponse = await axios.get(`${API}/contracts/${id}/kpi`);
        setKpi(kpiResponse.data);
      } catch (e) {}
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la création",
        variant: "destructive"
      });
    } finally {
      setCreatingRun(false);
    }
  };

  const handleStartRun = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/start`);
      toast({ title: "Succès", description: `${terminologySingular} ${isMasculine ? 'démarré' : 'démarrée'}` });
      fetchRuns();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleCompleteRun = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/complete`);
      toast({ title: "Succès", description: `${terminologySingular} ${isMasculine ? 'terminé' : 'terminée'}` });
      fetchRuns();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleCancelRun = async (runId, reason) => {
    if (!reason) {
      reason = window.prompt("Raison de l'annulation :");
      if (!reason) return;
    }
    try {
      await axios.post(`${API}/contract-runs/${runId}/cancel`, { reason });
      toast({ title: "Succès", description: `${terminologySingular} ${isMasculine ? 'annulé' : 'annulée'}` });
      fetchRuns();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  // Nouvelles fonctions de transition de statut détaillé
  const handleStartLoading = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/start-loading`);
      toast({ title: "Succès", description: "Chargement démarré" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleStartTransit = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/start-transit`);
      toast({ title: "Succès", description: "Transit démarré" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleStartUnloading = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/start-unloading`);
      toast({ title: "Succès", description: "Déchargement démarré" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleFinishUnloading = async (runId) => {
    try {
      await axios.post(`${API}/contract-runs/${runId}/finish-unloading`);
      toast({ title: "Succès", description: `${terminologySingular} ${isMasculine ? 'terminé' : 'terminée'}` });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  const handleUpdateWeight = async (runId, weight) => {
    try {
      await axios.patch(`${API}/contract-runs/${runId}/weight`, { weight: parseFloat(weight) });
      toast({ title: "Succès", description: `Poids mis à jour: ${weight} tonnes` });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  // Mise à jour du trajet (départ/arrivée)
  const handleUpdateTrajet = async (runId, trajetData) => {
    try {
      await axios.patch(`${API}/contract-runs/${runId}/trajet`, trajetData);
      toast({ title: "Succès", description: "Trajet mis à jour" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  // Mise à jour des ressources (véhicule/chauffeur)
  const handleUpdateResources = async (runId, resourcesData) => {
    try {
      await axios.patch(`${API}/contract-runs/${runId}/resources`, resourcesData);
      toast({ title: "Succès", description: "Ressources mises à jour" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    }
  };

  // Dupliquer le contrat
  const handleDuplicateContract = async () => {
    if (!window.confirm('Dupliquer ce contrat ? Une copie sera créée avec le statut "Brouillon".')) return;
    try {
      const response = await axios.post(`${API}/contracts/${contract.id}/duplicate`);
      toast({ title: "Succès", description: "Contrat dupliqué avec succès" });
      navigate(`/admin/contracts/${response.data.contract.id}`);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la duplication",
        variant: "destructive"
      });
    }
  };

  const handleDeleteContract = async () => {
    setDeletingContract(true);
    try {
      await axios.delete(`${API}/contracts/${contract.id}`);
      toast({ title: "Contrat supprimé", description: `${contract.contractNumber} a été supprimé définitivement.` });
      navigate('/admin/contracts');
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    } finally {
      setDeletingContract(false);
      setShowDeleteContractModal(false);
    }
  };

  // Dupliquer un transfert
  const handleDuplicateRun = async (runId, newDate = null) => {
    try {
      const params = newDate ? `?new_date=${newDate}` : '';
      const response = await axios.post(`${API}/contract-runs/${runId}/duplicate${params}`);
      toast({ title: "Succès", description: "Transfert dupliqué avec succès" });
      fetchRuns();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la duplication",
        variant: "destructive"
      });
    }
  };

  // Réparer un transfert orphelin
  const handleFixOrphanTransfer = async (runId) => {
    try {
      const res = await axios.post(`${API}/contract-runs/${runId}/fix-link`);
      toast({ title: "Succès", description: "Transfert réparé" });
      fetchRuns();
      if (expandedRunId === runId) {
        handleExpandRun(runId);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la réparation",
        variant: "destructive"
      });
    }
  };

  const handleValidatePOD = async (runId, stopId, approved, rejectionReason = null) => {
    setValidatingPOD(true);
    try {
      const params = new URLSearchParams({ approved: approved.toString() });
      if (rejectionReason) params.append('rejection_reason', rejectionReason);
      
      await axios.post(`${API}/contract-runs/${runId}/stops/${stopId}/pod/validate?${params}`);
      toast({ title: "Succès", description: approved ? "POD validé" : "POD rejeté" });
      fetchRuns();
      setShowPODModal(false);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    } finally {
      setValidatingPOD(false);
    }
  };

  const handleValidateFuel = async (runId, fuelId, approved) => {
    setValidatingFuel(true);
    try {
      await axios.post(`${API}/contract-runs/${runId}/fuel/${fuelId}/validate?approved=${approved}`);
      toast({ title: "Succès", description: approved ? "Carburant approuvé" : "Carburant rejeté" });
      fetchRuns();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur",
        variant: "destructive"
      });
    } finally {
      setValidatingFuel(false);
    }
  };

  const openPODModal = (run, stop) => {
    setSelectedRun(run);
    setSelectedStop(stop);
    setShowPODModal(true);
  };

  const handleExpandRun = async (runId) => {
    if (expandedRunId === runId) {
      // Collapse
      setExpandedRunId(null);
      setExpandedRunDetails(null);
      return;
    }
    
    // Expand and fetch full details
    setExpandedRunId(runId);
    setLoadingRunDetails(true);
    setExpandedRunDetails(null);
    
    try {
      const response = await axios.get(`${API}/contract-runs/${runId}`);
      setExpandedRunDetails(response.data.run || response.data);
    } catch (error) {
      console.error('Error fetching run details:', error);
      // Fallback to list data
      const run = runs.find(r => r.id === runId);
      setExpandedRunDetails(run);
    } finally {
      setLoadingRunDetails(false);
    }
  };

  const getRunStatusBadge = (status) => {
    const config = {
      planifiee: { label: 'Planifié', color: 'bg-slate-100 text-slate-700', icon: Clock },
      planned: { label: 'Planifié', color: 'bg-slate-100 text-slate-700', icon: Clock },
      en_cours_de_chargement: { label: 'Chargement', color: 'bg-blue-100 text-blue-700', icon: Package },
      en_transit: { label: 'En transit', color: 'bg-amber-100 text-amber-700', icon: Route },
      en_cours_de_dechargement: { label: 'Déchargement', color: 'bg-purple-100 text-purple-700', icon: MapPin },
      in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: Play },
      completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.planifiee;
    const Icon = c.icon;
    return (
      <Badge className={cn('flex items-center gap-1 px-2 py-0.5 text-xs', c.color)}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  // Filtrage et tri des runs
  const getFilteredAndSortedRuns = useCallback(() => {
    let filteredRuns = advancedFilteredRuns !== null ? [...advancedFilteredRuns] : [...runs];
    
    // Filtre par statut
    if (runsStatusFilter !== 'all') {
      filteredRuns = filteredRuns.filter(run => run.status === runsStatusFilter);
    }
    
    // Recherche textuelle
    if (runsSearchQuery.trim()) {
      const query = runsSearchQuery.toLowerCase().trim();
      filteredRuns = filteredRuns.filter(run => {
        const runNumber = (run.runNumber || '').toLowerCase();
        const reference = (run.reference || '').toLowerCase();
        const plate = (run.plannedVehiclePlate || run.vehicle?.plateNumber || run.vehicle?.plate || '').toLowerCase();
        const driver = (run.plannedDriverName || run.driver?.name || [run.driver?.firstName, run.driver?.lastName].filter(Boolean).join(' ') || '').toLowerCase();
        const merchandise = (run.merchandiseType || '').toLowerCase();
        const client = (run.clientName || '').toLowerCase();
        
        // Departure/arrival from stops
        const stops = run.stops || [];
        const departure = (run.departureAddress || stops[0]?.address || stops[0]?.city || '').toLowerCase();
        const arrival = (run.arrivalAddress || stops[stops.length - 1]?.address || stops[stops.length - 1]?.city || '').toLowerCase();
        
        return runNumber.includes(query) || 
               reference.includes(query) || 
               departure.includes(query) || 
               arrival.includes(query) ||
               plate.includes(query) ||
               driver.includes(query) ||
               merchandise.includes(query) ||
               client.includes(query);
      });
    }
    
    // Tri
    filteredRuns.sort((a, b) => {
      let aValue, bValue;
      
      switch (runsSortConfig.key) {
        case 'date':
          aValue = new Date(a.date || 0).getTime();
          bValue = new Date(b.date || 0).getTime();
          break;
        case 'reference':
          aValue = a.runNumber || a.reference || '';
          bValue = b.runNumber || b.reference || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'weight':
          aValue = a.weight || 0;
          bValue = b.weight || 0;
          break;
        default:
          aValue = a.date || '';
          bValue = b.date || '';
      }
      
      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return runsSortConfig.direction === 'asc' ? comparison : -comparison;
      }
      
      return runsSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    return filteredRuns;
  }, [runs, advancedFilteredRuns, runsSearchQuery, runsStatusFilter, runsSortConfig]);

  const handleRunsSort = (key) => {
    setRunsSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Liste des statuts disponibles pour le filtre
  const availableStatuses = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'planned', label: 'Planifié' },
    { value: 'planifiee', label: 'Planifié' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'en_cours_de_chargement', label: 'Chargement' },
    { value: 'en_transit', label: 'En transit' },
    { value: 'completed', label: 'Terminé' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  const getPODStatusBadge = (pod) => {
    if (!pod || !pod.captured) {
      return <Badge className="bg-gray-100 text-gray-600 text-xs">Non capturé</Badge>;
    }
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
      validated: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700' }
    };
    const c = statusConfig[pod.validationStatus] || statusConfig.pending;
    return <Badge className={cn('text-xs', c.color)}>{c.label}</Badge>;
  };

  const getAvailableStatusTransitions = () => {
    if (!contract) return [];
    
    const transitions = {
      'draft': [{ value: 'active', label: 'Activer', icon: Play }],
      'active': [
        { value: 'suspended', label: 'Suspendre', icon: Pause },
        { value: 'closed', label: 'Clôturer', icon: StopCircle }
      ],
      'suspended': [
        { value: 'active', label: 'Réactiver', icon: Play },
        { value: 'closed', label: 'Clôturer', icon: StopCircle }
      ],
      'closed': [{ value: 'restore', label: 'Restaurer (→ Actif)', icon: RotateCcw, isRestore: true }],
      'cancelled': [{ value: 'restore', label: 'Restaurer (→ Brouillon)', icon: RotateCcw, isRestore: true }]
    };
    
    return transitions[contract.status] || [];
  };

  // Bulk selection handlers
  const handleSelectRun = (runId, checked) => {
    if (checked) {
      setSelectedRunIds(prev => [...prev, runId]);
    } else {
      setSelectedRunIds(prev => prev.filter(id => id !== runId));
    }
  };

  const handleSelectAllRuns = (checked) => {
    if (checked) {
      // Select only FILTERED runs (not all runs)
      const displayed = getFilteredAndSortedRuns();
      setSelectedRunIds(displayed.map(r => r.id));
    } else {
      setSelectedRunIds([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRunIds.length === 0) return;
    
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/contract-runs/bulk-delete`, {
        run_ids: selectedRunIds
      });
      
      const result = response.data;
      toast({
        title: "Suppression terminée",
        description: `${result.deleted} transfert(s) supprimé(s)${result.skipped > 0 ? `, ${result.skipped} ignoré(s)` : ''}`
      });
      
      setSelectedRunIds([]);
      setShowBulkDeleteModal(false);
      fetchRuns();
      
      // Refresh KPI
      try {
        const kpiResponse = await axios.get(`${API}/contracts/${id}/kpi`);
        setKpi(kpiResponse.data);
      } catch (e) {}
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const getDeletableSelectedRuns = () => {
    return runs.filter(r => 
      selectedRunIds.includes(r.id) && 
      ['planned', 'planifiee', 'cancelled'].includes(r.status)
    );
  };

  // Bulk association handler
  const handleBulkAssociate = async () => {
    if (selectedRunIds.length === 0 || !associateTargetOperationId) return;
    
    setBulkAssociating(true);
    try {
      const response = await axios.post(`${API}/contract-runs/bulk-associate`, {
        run_ids: selectedRunIds,
        parent_operation_id: associateTargetOperationId
      });
      
      const result = response.data;
      toast({
        title: "Association terminée",
        description: result.message
      });
      
      setSelectedRunIds([]);
      setShowBulkAssociateModal(false);
      setAssociateTargetOperationId('');
      setAssociateSearchQuery('');
      fetchRuns();
      
      // Refresh KPI
      try {
        const kpiResponse = await axios.get(`${API}/contracts/${id}/kpi`);
        setKpi(kpiResponse.data);
      } catch (e) {}
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'association",
        variant: "destructive"
      });
    } finally {
      setBulkAssociating(false);
    }
  };

  const openBulkAssociateModal = () => {
    fetchAllParentOperations();
    setShowBulkAssociateModal(true);
  };

  // ── Bulk Assign Resources (Vehicle + Driver) ──
  const searchBulkVehicles = useCallback(async (q) => {
    setLoadingBulkVehicles(true);
    try {
      const res = await axios.get(`${API}/vehicles`, {
        params: { search: q, limit: 20 }});
      const items = Array.isArray(res.data) ? res.data : (res.data.data || res.data.items || []);
      setBulkVehicleOpts(items.map(v => {
        const semi = v.semiTrailer?.registration;
        const label = semi ? `${v.registration} / ${semi}` : v.registration;
        const typeLabels = { plateau: 'Plateau', fourgon: 'Fourgon', fourgon_frigo: 'Frigo', citerne: 'Citerne', benne: 'Benne', porte_conteneur: 'Porte-cont.', autre: 'Autre' };
        return {
          id: v.id, registration: v.registration, label,
          brand: v.brand || '', model: v.model || '',
          vehicleType: typeLabels[v.vehicleType] || v.vehicleType || '',
          capacity: v.capacity ? `${v.capacity}T` : '',
          status: v.status || 'disponible',
          partnerName: v.partnerName || '',
        };
      }));
    } catch { setBulkVehicleOpts([]); }
    finally { setLoadingBulkVehicles(false); }
  }, []);

  const searchBulkDrivers = useCallback(async (q) => {
    setLoadingBulkDrivers(true);
    try {
      const res = await axios.get(`${API}/drivers`, {
        params: { search: q, limit: 20 }});
      const items = Array.isArray(res.data) ? res.data : (res.data.data || res.data.items || []);
      setBulkDriverOpts(items.map(d => ({
        id: d.id,
        name: `${d.firstName || ''} ${d.lastName || ''}`.trim(),
        phone: d.phone || ''
      })));
    } catch { setBulkDriverOpts([]); }
    finally { setLoadingBulkDrivers(false); }
  }, []);

  // Debounced search - only search when there's actual input
  useEffect(() => {
    if (!showBulkAssignModal) return;
    if (bulkVehicleQ.length < 2) { setBulkVehicleOpts([]); return; }
    const t = setTimeout(() => searchBulkVehicles(bulkVehicleQ), 300);
    return () => clearTimeout(t);
  }, [bulkVehicleQ, showBulkAssignModal, searchBulkVehicles]);

  useEffect(() => {
    if (!showBulkAssignModal) return;
    if (bulkDriverQ.length < 2) { setBulkDriverOpts([]); return; }
    const t = setTimeout(() => searchBulkDrivers(bulkDriverQ), 300);
    return () => clearTimeout(t);
  }, [bulkDriverQ, showBulkAssignModal, searchBulkDrivers]);

  const handleBulkAssignResources = async () => {
    if (!bulkAssignVehicle && !bulkAssignDriver) {
      toast({ title: "Erreur", description: "Vehicule ou chauffeur requis", variant: "destructive" });
      return;
    }
    setBulkAssigning(true);
    try {
      const payload = { run_ids: selectedRunIds };
      if (bulkAssignVehicle) {
        payload.vehicle_plate = bulkAssignVehicle.label;
        payload.vehicle_id = bulkAssignVehicle.id;
      }
      if (bulkAssignDriver) {
        payload.driver_name = bulkAssignDriver.name;
        payload.driver_id = bulkAssignDriver.id;
        payload.driver_phone = bulkAssignDriver.phone;
      }
      const res = await axios.post(`${API}/contract-runs/bulk-assign-resources`, payload, {});
      toast({
        title: "Assignation en masse",
        description: `${res.data.success_count} transfert(s) mis a jour${res.data.failed_count > 0 ? `, ${res.data.failed_count} echoue(s)` : ''}`
      });
      setShowBulkAssignModal(false);
      setBulkAssignVehicle(null);
      setBulkAssignDriver(null);
      setBulkVehicleQ('');
      setBulkDriverQ('');
      setSelectedRunIds([]);
      fetchRuns();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Erreur lors de l'assignation", variant: "destructive" });
    } finally {
      setBulkAssigning(false);
    }
  };

  const filteredAssociateOperations = allParentOperations.filter(op => {
    if (!associateSearchQuery) return true;
    const q = associateSearchQuery.toLowerCase();
    return (
      (op.operationNumber || '').toLowerCase().includes(q) ||
      (op.departure || '').toLowerCase().includes(q) ||
      (op.arrival || '').toLowerCase().includes(q) ||
      (`${op.firstName || ''} ${op.lastName || ''}`).toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contrat non trouvé</p>
        <Button onClick={() => navigate('/admin/contracts')} className="mt-4">
          Retour à la liste
        </Button>
      </div>
    );
  }

  const terminologyLabel = TERMINOLOGY_CONFIG[contract.terminology]?.plural || 'Opérations';
  const terminologySingular = TERMINOLOGY_CONFIG[contract.terminology]?.label || 'Opération';
  const isMasculine = TERMINOLOGY_CONFIG[contract.terminology]?.gender === 'masculine';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/contracts')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900">
                {contract.contractNumber || 'Nouveau Contrat'}
              </h1>
              <ContractStatusBadge status={contract.status} />
            </div>
            <p className="text-gray-500">{contract.clientName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Bouton Modifier */}
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/contracts/${contract.id}/edit`)}
            data-testid="edit-contract-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Modifier
          </Button>
          
          {/* Bouton Dupliquer */}
          <Button
            variant="outline"
            onClick={handleDuplicateContract}
            data-testid="duplicate-contract-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Dupliquer
          </Button>

          {/* Bouton Supprimer — permission contracts.delete (admin + lead commercial), action auditée */}
          {hasPermission('contracts.delete') && (
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              onClick={() => setShowDeleteContractModal(true)}
              data-testid="delete-contract-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
          
          {/* Actions de statut */}
          {getAvailableStatusTransitions().map((transition) => {
            const Icon = transition.icon;
            const disabled = transition.requires && !contract[transition.requires];
            
            return (
              <Button
                key={transition.value}
                variant="outline"
                onClick={() => {
                  setNewStatus(transition.value);
                  setShowStatusModal(true);
                }}
                disabled={disabled}
                title={disabled ? 'Document signé requis' : ''}
                data-testid={`status-${transition.value}-btn`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {transition.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{terminologyLabel}</p>
                  <p className="text-2xl font-bold">{kpi.runsTotal}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Taux de service</p>
                  <p className="text-2xl font-bold">{kpi.serviceRate}%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">CA Facturé</p>
                  <p className="text-2xl font-bold">{formatPrice(kpi.totalInvoiced)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Impayés</p>
                  <p className="text-2xl font-bold text-amber-600">{formatPrice(kpi.totalUnpaid)}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="hierarchy">Hiérarchie</TabsTrigger>
          <TabsTrigger value="operations">{terminologyLabel} ({kpi?.runsTotal || 0})</TabsTrigger>
          <TabsTrigger value="calendar">Calendrier</TabsTrigger>
          <TabsTrigger value="resources">Ressources ({(contract.dedicatedVehicles?.length || 0) + (contract.dedicatedDrivers?.length || 0)})</TabsTrigger>
          <TabsTrigger value="billing">Facturation</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="quotes-tab-trigger">Devis</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="attendance-tab-trigger">Présence</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="analytics-tab-trigger">Analyse</TabsTrigger>
          {hasPermission('contract_margin.view.all') && (
            <TabsTrigger value="margin" data-testid="margin-tab-trigger">Rentabilité</TabsTrigger>
          )}
          {hasPermission('contract_margin.view.all') && (
            <TabsTrigger value="prefinancing" data-testid="prefinancing-tab-trigger">Préfinancements</TabsTrigger>
          )}
          {(hasPermission('contracts.view.all') || hasPermission('contract_runs.view.all')) && (
            <TabsTrigger value="compliance" data-testid="compliance-tab-trigger">Suivi</TabsTrigger>
          )}
          <TabsTrigger value="history">Historique</TabsTrigger>
          <TabsTrigger value="notes" data-testid="contract-tab-internal-notes">
            Notes
            {contractNotesCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded-full font-semibold"
                    data-testid="contract-internal-notes-count">
                {contractNotesCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-400" />
                  Client
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Building className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{contract.clientName}</p>
                    {contract.clientCode && <p className="text-sm text-gray-500">{contract.clientCode}</p>}
                  </div>
                </div>
                
                {contract.clientContact && (
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-sm font-medium text-gray-700">Contact</p>
                    <div className="space-y-1 text-sm">
                      {contract.clientContact.name && (
                        <p className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {contract.clientContact.name}
                        </p>
                      )}
                      {contract.clientContact.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {contract.clientContact.phone}
                        </p>
                      )}
                      {contract.clientContact.email && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {contract.clientContact.email}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transport Partner (if assigned) */}
            {contract.transportPartnerName && (
              <Card data-testid="contract-partner-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-500" />
                    Partenaire Transport
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center">
                      <Truck className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{contract.transportPartnerName}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contract Period */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  Période
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Début</p>
                    <p className="font-medium">{formatDate(contract.startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fin</p>
                    <p className="font-medium">{formatDate(contract.endDate)}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Renouvellement auto</p>
                      <p className="font-medium">{contract.autoRenew ? 'Oui' : 'Non'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Terminologie</p>
                      <Badge variant="outline">{terminologyLabel}</Badge>
                    </div>
                  </div>
                  {contract.createdByName && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-gray-500" data-testid="contract-created-by">
                      <User className="w-3 h-3" />
                      <span>Créé par <span className="font-medium text-gray-700">{contract.createdByName}</span></span>
                      {contract.createdAt && (
                        <span>· {new Date(contract.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Document */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  Document Signé
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contract.signedDocument ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <FileText className="w-8 h-8 text-green-600" />
                      <div className="flex-1">
                        <p className="font-medium text-green-800">{contract.signedDocument.fileName}</p>
                        <p className="text-sm text-green-600">
                          Uploadé le {formatDate(contract.signedDocument.uploadedAt)}
                        </p>
                        {contract.signedDocument.signedDate && (
                          <p className="text-sm text-green-600">
                            Signé le {formatDate(contract.signedDocument.signedDate)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleDownloadDocument} data-testid="download-doc-btn">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </Button>
                      {contract.status === 'draft' && (
                        <Button variant="outline" onClick={handleDeleteDocument} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <FileUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">Aucun document uploadé</p>
                    <Button onClick={() => setShowUploadModal(true)} data-testid="upload-doc-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Uploader le contrat signé
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {contract.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 whitespace-pre-wrap">{contract.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Contacts d'escalade */}
            <Card className="lg:col-span-2" data-testid="escalation-contacts-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-gray-400" />
                  Contacts d'escalade
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EscalationContactsEditor
                  value={contract.escalationContacts || {}}
                  contractId={contract.id}
                  onSaved={(val) => setContract(prev => ({...prev, escalationContacts: val}))}
                />
              </CardContent>
            </Card>

            {/* Operateurs assignes */}
            <Card className="lg:col-span-2" data-testid="assigned-operators-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-gray-400" />
                  Operateurs assignes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AssignedOperatorsEditor
                  value={contract.assignedOperators || []}
                  contractId={contract.id}
                  onSaved={(val) => setContract(prev => ({...prev, assignedOperators: val}))}
                />
              </CardContent>
            </Card>
          </div>

          {/* ─── Fleet Summary (fleet_location mode) — shown in overview ─── */}
          {contract.billingRules?.billingMode === 'fleet_location' && (
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Truck className="w-5 h-5" />
                    Flotte mobilisée
                    <span className="ml-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {(contract.billingRules?.fleetItems || []).reduce((s, i) => s + (i.quantity || 0), 0)} camion{(contract.billingRules?.fleetItems || []).reduce((s, i) => s + (i.quantity || 0), 0) > 1 ? 's' : ''}
                    </span>
                  </CardTitle>
                  {hasPermission('contracts.edit') && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const current = contract.billingRules?.fleetItems || [];
                      setFleetItems(current.length > 0 ? JSON.parse(JSON.stringify(current)) : [
                        { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
                      ]);
                      setFleetAmendmentDate(new Date().toISOString().split('T')[0]);
                      setFleetAmendmentReason('');
                      setShowFleetModal(true);
                    }} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      Modifier la flotte
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(contract.billingRules?.fleetItems || []).length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucun type de camion défini.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-3">
                      <div className="col-span-5">Type de camion</div>
                      <div className="col-span-2 text-center">Qté</div>
                      <div className="col-span-3 text-right">Tarif/camion</div>
                      <div className="col-span-2 text-right">Sous-total</div>
                    </div>
                    {(contract.billingRules?.fleetItems || []).map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-blue-50 rounded-lg px-3 py-2.5">
                        <div className="col-span-5 font-medium text-gray-800 flex items-center gap-1.5">
                          <span>{item.vehicleTypeLabel || item.vehicleType}</span>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="bg-blue-200 text-blue-900 font-bold text-sm px-2 py-0.5 rounded-full">×{item.quantity}</span>
                        </div>
                        <div className="col-span-3 text-right text-sm text-gray-600">
                          {new Intl.NumberFormat('fr-FR').format(item.monthlyRatePerTruck)} FCFA
                        </div>
                        <div className="col-span-2 text-right text-sm font-semibold text-blue-800">
                          {new Intl.NumberFormat('fr-FR').format((item.quantity || 0) * (item.monthlyRatePerTruck || 0))}
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 border-t border-blue-200 px-3">
                      <p className="text-sm font-medium text-gray-600">Redevance mensuelle totale</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {new Intl.NumberFormat('fr-FR').format(contract.billingRules?.totalMonthlyFleetAmount || 0)}
                        <span className="text-sm font-normal text-gray-500 ml-1">FCFA/mois</span>
                      </p>
                    </div>
                    {contract.fleetAmendments?.length > 0 && (
                      <p className="text-xs text-blue-600 text-center pt-1 cursor-pointer hover:underline" onClick={() => setActiveTab('history')}>
                        {contract.fleetAmendments.length} avenant{contract.fleetAmendments.length > 1 ? 's' : ''} enregistré{contract.fleetAmendments.length > 1 ? 's' : ''} → voir l'historique
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="hierarchy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Vue Hiérarchique
              </CardTitle>
              <p className="text-sm text-gray-500">
                Visualisez la structure complète : Contrat → Opérations → Transferts
              </p>
            </CardHeader>
            <CardContent>
              <HierarchyView
                contractId={id}
                contractNumber={contract.contractNumber}
                runs={runs}
                onRefresh={fetchRuns}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                {terminologyLabel} ({runsStatusFilter !== 'all' || runsSearchQuery ? `${getFilteredAndSortedRuns().length} / ${runs.length}` : (kpi?.runsTotal || runs.length)})
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={fetchRuns}
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                {contract.status === 'active' && (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => setShowCSVImportModal(true)}
                      data-testid="import-csv-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Importer CSV
                    </Button>
                    <Button 
                      onClick={() => setShowCreateRunModal(true)}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="create-run-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {isMasculine ? 'Nouveau' : 'Nouvelle'} {terminologySingular}
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {contract.status !== 'active' ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Le contrat doit être actif pour créer des {terminologyLabel.toLowerCase()}</p>
                </div>
              ) : loadingRuns ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : runs.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 mb-4">{isMasculine ? 'Aucun' : 'Aucune'} {terminologySingular.toLowerCase()} pour le moment</p>
                  <Button 
                    onClick={() => setShowCreateRunModal(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer {isMasculine ? 'le premier' : 'la première'} {terminologySingular.toLowerCase()}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Advanced Search Panel */}
                  <div className="mb-4">
                    <AdvancedSearchPanel
                      operations={runs}
                      onFilter={(results) => setAdvancedFilteredRuns(results)}
                      onReset={() => setAdvancedFilteredRuns(null)}
                      statusOptions={[
                        { value: 'planned', label: 'Planifie' },
                        { value: 'planifiee', label: 'Planifie' },
                        { value: 'loading', label: 'Chargement' },
                        { value: 'in_transit', label: 'En transit' },
                        { value: 'in_progress', label: 'En cours' },
                        { value: 'completed', label: 'Termine' },
                        { value: 'cancelled', label: 'Annule' },
                      ]}
                    />
                  </div>

                  {/* Barre de recherche, filtre et tri */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-4 p-3 bg-gray-50 rounded-lg border">
                    {/* Recherche */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Rechercher par référence, trajet, véhicule, chauffeur..."
                        value={runsSearchQuery}
                        onChange={(e) => setRunsSearchQuery(e.target.value)}
                        className="pl-10 bg-white"
                        data-testid="runs-search-input"
                      />
                    </div>
                    
                    {/* Filtre par statut */}
                    <Select value={runsStatusFilter} onValueChange={setRunsStatusFilter}>
                      <SelectTrigger className="w-full sm:w-[180px] bg-white" data-testid="runs-status-filter">
                        <Filter className="w-4 h-4 mr-2 text-gray-400" />
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Tri */}
                    <Select 
                      value={`${runsSortConfig.key}-${runsSortConfig.direction}`}
                      onValueChange={(v) => {
                        const [key, direction] = v.split('-');
                        setRunsSortConfig({ key, direction });
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-[180px] bg-white" data-testid="runs-sort-select">
                        <ArrowUpDown className="w-4 h-4 mr-2 text-gray-400" />
                        <SelectValue placeholder="Trier par" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date-desc">Date (récent → ancien)</SelectItem>
                        <SelectItem value="date-asc">Date (ancien → récent)</SelectItem>
                        <SelectItem value="reference-asc">Référence (A → Z)</SelectItem>
                        <SelectItem value="reference-desc">Référence (Z → A)</SelectItem>
                        <SelectItem value="status-asc">Statut (A → Z)</SelectItem>
                        <SelectItem value="weight-desc">Poids (lourd → léger)</SelectItem>
                        <SelectItem value="weight-asc">Poids (léger → lourd)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Résumé des filtres actifs */}
                  {(runsSearchQuery || runsStatusFilter !== 'all') && (
                    <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                      <span>{getFilteredAndSortedRuns().length} résultat(s)</span>
                      {runsSearchQuery && (
                        <Badge variant="secondary" className="text-xs">
                          Recherche: "{runsSearchQuery}"
                          <X 
                            className="w-3 h-3 ml-1 cursor-pointer hover:text-red-500" 
                            onClick={() => setRunsSearchQuery('')}
                          />
                        </Badge>
                      )}
                      {runsStatusFilter !== 'all' && (
                        <Badge variant="secondary" className="text-xs">
                          Statut: {availableStatuses.find(s => s.value === runsStatusFilter)?.label}
                          <X 
                            className="w-3 h-3 ml-1 cursor-pointer hover:text-red-500" 
                            onClick={() => setRunsStatusFilter('all')}
                          />
                        </Badge>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-xs"
                        onClick={() => { setRunsSearchQuery(''); setRunsStatusFilter('all'); }}
                      >
                        Réinitialiser
                      </Button>
                    </div>
                  )}

                  {/* Bulk actions bar */}
                  {selectedRunIds.length > 0 && (
                    <div className="flex items-center justify-between p-3 mb-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="bulk-actions-bar">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedRunIds.length === getFilteredAndSortedRuns().length}
                          onCheckedChange={(checked) => handleSelectAllRuns(checked)}
                          data-testid="select-all-runs"
                        />
                        <span className="text-sm font-medium text-slate-800">
                          {selectedRunIds.length} {selectedRunIds.length === 1 ? 'sélectionné' : 'sélectionnés'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowBulkStatusModal(true)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid="bulk-change-status-btn"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2 rotate-90" />
                          Changer statut ({selectedRunIds.length})
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openBulkAssociateModal}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid="bulk-associate-btn"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Associer ({selectedRunIds.length})
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setBulkAssignVehicle(null); setBulkAssignDriver(null); setBulkVehicleQ(''); setBulkDriverQ(''); setShowBulkAssignModal(true); }}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid="bulk-assign-resources-btn"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Vehicule / Chauffeur ({selectedRunIds.length})
                        </Button>
                        {getDeletableSelectedRuns().length > 0 && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowBulkDeleteModal(true)}
                            data-testid="bulk-delete-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer ({getDeletableSelectedRuns().length})
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <Checkbox
                            checked={selectedRunIds.length > 0 && selectedRunIds.length === getFilteredAndSortedRuns().length}
                            onCheckedChange={(checked) => handleSelectAllRuns(checked)}
                            data-testid="header-select-all"
                          />
                        </TableHead>
                        <TableHead className="w-[120px]">Date</TableHead>
                        <TableHead>Référence</TableHead>
                        <TableHead>Trajet</TableHead>
                        <TableHead className="w-[80px] text-right">Distance</TableHead>
                        <TableHead className="w-[80px]">Poids estimé</TableHead>
                        <TableHead className="w-[90px] text-emerald-700">Poids net</TableHead>
                        <TableHead className="w-[80px] text-center">{terminologyLabel}</TableHead>
                        <TableHead className="w-[140px]">Statut</TableHead>
                        <TableHead>Véhicule / Chauffeur</TableHead>
                        <TableHead className="w-[140px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const allFilteredRuns = getFilteredAndSortedRuns();
                        const totalPages = Math.max(1, Math.ceil(allFilteredRuns.length / runsPerPage));
                        const startIdx = (runsCurrentPage - 1) * runsPerPage;
                        return allFilteredRuns.slice(startIdx, startIdx + runsPerPage);
                      })().map((run) => (
                        <React.Fragment key={run.id}>
                          <TableRow 
                            className={cn(
                              "cursor-pointer hover:bg-gray-50",
                              selectedRunIds.includes(run.id) && "bg-red-50"
                            )}
                            onClick={() => navigate(`/admin/transfers/${run.id}`)}
                            data-testid={`run-row-${run.id}`}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedRunIds.includes(run.id)}
                                onCheckedChange={(checked) => handleSelectRun(run.id, checked)}
                                data-testid={`select-run-${run.id}`}
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              <div>
                                <p className="font-medium">{formatDate(run.date)}</p>
                                <p className="text-xs text-gray-500">{run.shift === 'day' ? 'Jour' : 'Nuit'}</p>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              <div className="flex items-center gap-2">
                                {expandedRunId === run.id ? (
                                  <XCircle className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <Eye className="w-4 h-4 text-gray-400" />
                                )}
                                <span>{run.runNumber || `#${run.id.slice(0, 8)}`}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {run.stops?.length > 0 ? (() => {
                                // PM Spec 03/06/2026 — Format inline "Départ → Étape1 → Étape2 → Arrivée"
                                const cities = run.stops
                                  .map(s => s.city || s.address?.split(',')[0])
                                  .filter(Boolean);
                                if (cities.length === 0) {
                                  return <span className="text-gray-400 text-sm">Non défini</span>;
                                }
                                const full = cities.join(' → ');
                                return (
                                  <div
                                    className="text-sm text-gray-700 truncate max-w-[280px]"
                                    title={full}
                                    data-testid={`run-${run.id}-trajet`}
                                  >
                                    {full}
                                  </div>
                                );
                              })() : (
                                <span className="text-gray-400 text-sm">Non défini</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-gray-600" data-testid={`run-${run.id}-distance`}>
                              {run.distance_km ? `${Math.round(run.distance_km)} km` : <span className="text-gray-300">—</span>}
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {run.weight ? `${run.weight} T` : '-'}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-700">
                              {run.netWeight != null && run.netWeight > 0
                                ? `${run.netWeight} T`
                                : run.tripsNetWeights?.length > 0
                                  ? `${run.tripsNetWeights.reduce((s, w) => s + (w || 0), 0).toFixed(1)} T`
                                  : <span className="text-gray-300">—</span>
                              }
                            </TableCell>
                            <TableCell className="text-center">
                              {run.numberOfTrips && run.numberOfTrips > 1 ? (
                                <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700">
                                  {run.numberOfTrips}
                                  <span className="text-xs font-normal text-gray-400">A/R</span>
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">1</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getRunStatusBadge(run.status)}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="truncate max-w-[150px]">
                                  {run.plannedVehiclePlate || <span className="text-gray-400">Non assigné</span>}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-[150px]">
                                  {run.plannedDriverName || 'Chauffeur non assigné'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    if (run.linkedOperationId) {
                                      navigate(`/admin/operations/${run.linkedOperationId}`);
                                    }
                                  }}
                                  className="h-8 px-2"
                                  title={run.linkedOperationId ? "Voir opération liée" : "Aucune opération liée"}
                                  disabled={!run.linkedOperationId}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDuplicateRun(run.id)}
                                  className="h-8 px-2"
                                  title="Dupliquer ce transfert"
                                  data-testid={`duplicate-run-${run.id}`}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                {run.stops?.some(s => s.pod?.captured && s.pod?.validationStatus === 'pending') && (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs px-1">POD</Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Row Content */}
                          {expandedRunId === run.id && (
                            <TableRow className="bg-gray-50">
                              <TableCell colSpan={8} className="p-0">
                                <div className="p-4 space-y-4 border-t border-b border-gray-200">
                                  {loadingRunDetails ? (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                    </div>
                                  ) : (
                                    <>
                          {/* Actions - Workflow détaillé */}
                          <div className="flex flex-wrap gap-2 pb-4 border-b">
                            {/* Planifié -> Chargement */}
                            {['planned', 'planifiee'].includes(run.status) && (
                              <Button 
                                size="sm" 
                                onClick={() => handleStartLoading(run.id)}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <Package className="w-4 h-4 mr-1" />
                                Démarrer chargement
                              </Button>
                            )}
                            {/* Chargement -> Transit */}
                            {run.status === 'en_cours_de_chargement' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleStartTransit(run.id)}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                <Route className="w-4 h-4 mr-1" />
                                Départ (en transit)
                              </Button>
                            )}
                            {/* Transit -> Déchargement */}
                            {run.status === 'en_transit' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleStartUnloading(run.id)}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <MapPin className="w-4 h-4 mr-1" />
                                Arrivée (déchargement)
                              </Button>
                            )}
                            {/* Déchargement -> Terminé */}
                            {run.status === 'en_cours_de_dechargement' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleFinishUnloading(run.id)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Terminer
                              </Button>
                            )}
                            {/* Legacy: in_progress -> completed */}
                            {run.status === 'in_progress' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleCompleteRun(run.id)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Terminer
                              </Button>
                            )}
                            {/* Annuler (visible tant que pas terminé ou annulé) */}
                            {!['completed', 'cancelled'].includes(run.status) && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handleCancelRun(run.id)}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Annuler
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                if (run.linkedOperationId) {
                                  navigate(`/admin/operations/${run.linkedOperationId}`);
                                } else {
                                  toast({
                                    title: "Information",
                                    description: "Ce transfert n'est pas lié à une opération",
                                    variant: "default"
                                  });
                                }
                              }}
                              disabled={!run.linkedOperationId}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir opération liée
                            </Button>
                          </div>

                          {/* Poids et documents */}
                          <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                              <Label className="text-sm text-gray-500">Poids (tonnes)</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input 
                                  type="number" 
                                  step="0.1"
                                  placeholder="Ex: 12.5"
                                  defaultValue={expandedRunDetails?.weight || run.weight || ''}
                                  className="w-24 h-8"
                                  onBlur={(e) => {
                                    if (e.target.value && e.target.value !== (expandedRunDetails?.weight || run.weight || '').toString()) {
                                      handleUpdateWeight(run.id, e.target.value);
                                    }
                                  }}
                                />
                                <Weight className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm text-gray-500">Documents</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="text-xs bg-blue-100 text-blue-700">
                                  <FileCheck className="w-3 h-3 mr-1" />
                                  Charg.: {(expandedRunDetails?.loadingDocuments || run.loadingDocuments || []).length}
                                </Badge>
                                <Badge className="text-xs bg-purple-100 text-purple-700">
                                  <FileCheck className="w-3 h-3 mr-1" />
                                  Déch.: {(expandedRunDetails?.unloadingDocuments || run.unloadingDocuments || []).length}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {/* Trajet et Ressources - Éditable */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-4 border-b">
                            {/* Trajet */}
                            <div className="space-y-3">
                              <h4 className="font-medium flex items-center gap-2 text-sm">
                                <Route className="w-4 h-4" />
                                Trajet
                                <span className="text-xs text-gray-400">(modifiable)</span>
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-1">D</div>
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-500 mb-1">Départ</p>
                                    <Input 
                                      type="text"
                                      placeholder="Adresse de départ"
                                      defaultValue={expandedRunDetails?.stops?.[0]?.address || run.stops?.[0]?.address || ''}
                                      className="h-8 text-sm"
                                      onBlur={(e) => {
                                        const currentVal = expandedRunDetails?.stops?.[0]?.address || run.stops?.[0]?.address || '';
                                        if (e.target.value !== currentVal) {
                                          handleUpdateTrajet(run.id, { departure_address: e.target.value });
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="flex items-start gap-2 p-2 bg-emerald-50 rounded">
                                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-1">A</div>
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-500 mb-1">Arrivée</p>
                                    <Input 
                                      type="text"
                                      placeholder="Adresse d'arrivée"
                                      defaultValue={
                                        expandedRunDetails?.stops?.[expandedRunDetails.stops.length - 1]?.address || 
                                        run.stops?.[run.stops?.length - 1]?.address || ''
                                      }
                                      className="h-8 text-sm"
                                      onBlur={(e) => {
                                        const stops = expandedRunDetails?.stops || run.stops || [];
                                        const currentVal = stops[stops.length - 1]?.address || '';
                                        if (e.target.value !== currentVal) {
                                          handleUpdateTrajet(run.id, { arrival_address: e.target.value });
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Véhicule / Chauffeur */}
                            <div className="space-y-3">
                              <h4 className="font-medium flex items-center gap-2 text-sm">
                                <Truck className="w-4 h-4" />
                                Ressources
                                <span className="text-xs text-gray-400">(modifiable)</span>
                              </h4>
                              <div className="space-y-2">
                                <div className="p-2 bg-gray-50 rounded">
                                  <p className="text-xs text-gray-500 mb-1">Véhicule (plaque)</p>
                                  <Input 
                                    type="text"
                                    placeholder="Ex: CI 1234 AB"
                                    defaultValue={expandedRunDetails?.plannedVehiclePlate || run.plannedVehiclePlate || ''}
                                    className="h-8 text-sm"
                                    onBlur={(e) => {
                                      const currentVal = expandedRunDetails?.plannedVehiclePlate || run.plannedVehiclePlate || '';
                                      if (e.target.value !== currentVal) {
                                        handleUpdateResources(run.id, { vehicle_plate: e.target.value });
                                      }
                                    }}
                                  />
                                </div>
                                <div className="p-2 bg-gray-50 rounded">
                                  <p className="text-xs text-gray-500 mb-1">Chauffeur (nom)</p>
                                  <Input 
                                    type="text"
                                    placeholder="Nom du chauffeur"
                                    defaultValue={expandedRunDetails?.plannedDriverName || run.plannedDriverName || ''}
                                    className="h-8 text-sm"
                                    onBlur={(e) => {
                                      const currentVal = expandedRunDetails?.plannedDriverName || run.plannedDriverName || '';
                                      if (e.target.value !== currentVal) {
                                        handleUpdateResources(run.id, { driver_name: e.target.value });
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bouton de réparation si transfert orphelin */}
                          {!run.linkedOperationId && (
                            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-amber-600" />
                              <span className="text-sm text-amber-700 flex-1">Ce transfert n'est pas lié à une opération</span>
                              <Button
                                size="sm"
                                onClick={() => handleFixOrphanTransfer(run.id)}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                Réparer le lien
                              </Button>
                            </div>
                          )}

                          {/* Fuel Entries */}
                          {(expandedRunDetails?.fuelEntries?.length > 0 || run.fuelEntries?.length > 0) && (
                            <div>
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <DollarSign className="w-4 h-4" />
                                Carburant ({(expandedRunDetails?.fuelEntries || run.fuelEntries || []).length})
                              </h4>
                              <div className="space-y-2">
                                {(expandedRunDetails?.fuelEntries || run.fuelEntries || []).map((fuel) => (
                                  <div 
                                    key={fuel.id} 
                                    className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                                  >
                                    <div>
                                      <p className="font-medium">{formatPrice(fuel.amount)}</p>
                                      <p className="text-xs text-gray-500">
                                        {fuel.liters ? `${fuel.liters}L • ` : ''}{fuel.station || 'Station non précisée'}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {fuel.validated ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Validé</Badge>
                                      ) : (
                                        <>
                                          <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => handleValidateFuel(run.id, fuel.id, true)}
                                            disabled={validatingFuel}
                                            className="h-7 px-2 text-green-600"
                                          >
                                            <CheckCircle className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => handleValidateFuel(run.id, fuel.id, false)}
                                            disabled={validatingFuel}
                                            className="h-7 px-2 text-red-600"
                                          >
                                            <XCircle className="w-3 h-3" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-2 text-sm text-right">
                                <span className="text-gray-500">Avance: </span>
                                <span className="font-medium">{formatPrice(expandedRunDetails?.fuelTotalAdvanced || run.fuelTotalAdvanced || 0)}</span>
                                <span className="mx-2">|</span>
                                <span className="text-gray-500">Justifié: </span>
                                <span className="font-medium">{formatPrice(expandedRunDetails?.fuelTotalJustified || run.fuelTotalJustified || 0)}</span>
                              </div>
                            </div>
                          )}
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                      {/* Message si aucun résultat après filtrage */}
                      {getFilteredAndSortedRuns().length === 0 && (runsSearchQuery || runsStatusFilter !== 'all') && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8">
                            <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                            <p className="text-gray-500 mb-2">Aucun résultat trouvé</p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => { setRunsSearchQuery(''); setRunsStatusFilter('all'); }}
                            >
                              Réinitialiser les filtres
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {/* Pagination Transferts */}
                  {(() => {
                    const allFilteredRuns = getFilteredAndSortedRuns();
                    const totalItems = allFilteredRuns.length;
                    const totalPages = Math.max(1, Math.ceil(totalItems / runsPerPage));
                    const startIdx = (runsCurrentPage - 1) * runsPerPage;
                    const endIdx = Math.min(startIdx + runsPerPage, totalItems);
                    if (totalItems === 0) return null;
                    const pageNumbers = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, runsCurrentPage - 2);
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
                    for (let p = startPage; p <= endPage; p++) pageNumbers.push(p);
                    return (
                      <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-2 py-3 border-t border-gray-100 mt-2" data-testid="runs-pagination">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span data-testid="runs-pagination-count">
                            {totalItems > 0 ? `Affichage de ${startIdx + 1} à ${endIdx} sur ${totalItems}` : 'Aucun élément'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Par page</span>
                            <select
                              value={runsPerPage}
                              onChange={(e) => setRunsPerPage(Number(e.target.value))}
                              className="text-sm border rounded px-2 py-1 bg-white hover:bg-gray-50 cursor-pointer"
                              data-testid="runs-per-page-select"
                            >
                              {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {totalPages > 1 && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={runsCurrentPage === 1}
                              onClick={() => setRunsCurrentPage(1)}
                              data-testid="runs-page-first"
                            >«</Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={runsCurrentPage === 1}
                              onClick={() => setRunsCurrentPage((p) => Math.max(1, p - 1))}
                              data-testid="runs-page-prev"
                            >‹</Button>
                            {startPage > 1 && <span className="px-2 text-gray-400">…</span>}
                            {pageNumbers.map((p) => (
                              <Button
                                key={p}
                                size="sm"
                                variant={p === runsCurrentPage ? 'default' : 'outline'}
                                onClick={() => setRunsCurrentPage(p)}
                                data-testid={`runs-page-${p}`}
                                className={p === runsCurrentPage ? 'bg-red-600 hover:bg-red-700' : ''}
                              >{p}</Button>
                            ))}
                            {endPage < totalPages && <span className="px-2 text-gray-400">…</span>}
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={runsCurrentPage === totalPages}
                              onClick={() => setRunsCurrentPage((p) => Math.min(totalPages, p + 1))}
                              data-testid="runs-page-next"
                            >›</Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={runsCurrentPage === totalPages}
                              onClick={() => setRunsCurrentPage(totalPages)}
                              data-testid="runs-page-last"
                            >»</Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-6">
          <TransferCalendar contractId={id} />
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="space-y-6">
          {/* Show partner context if contract has a partner */}
          {contract.transportPartnerName && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="resource-partner-context">
              <Truck className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Partenaire : {contract.transportPartnerName}</p>
                <p className="text-xs text-amber-600">
                  {partnerVehicles.length} véhicule{partnerVehicles.length !== 1 ? 's' : ''} et {partnerDrivers.length} chauffeur{partnerDrivers.length !== 1 ? 's' : ''} liés à ce partenaire
                </p>
              </div>
            </div>
          )}

          {/* --- UNIFIED RESOURCES PANEL --- */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* === VEHICLES PANEL === */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Truck className="w-5 h-5 text-gray-400" />
                    Véhicules ({contract.dedicatedVehicles?.length || 0})
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => { fetchResources(); setVehicleSearch(''); setResourceTab('all'); setShowVehicleModal(true); }} data-testid="add-vehicle-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Depuis le parc
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Assigned vehicles */}
                {contract.dedicatedVehicles?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignés au contrat</p>
                    {contract.dedicatedVehicles.map((vehicle, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`assigned-vehicle-${index}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center">
                            <Truck className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{vehicle.plate}</p>
                            <p className="text-xs text-gray-500">{vehicle.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">Assigné</Badge>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveVehicle(vehicle.vehicleId)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Partner vehicles — Quick assign */}
                {contract.transportPartnerName && partnerVehicles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Flotte de {contract.transportPartnerName}
                    </p>
                    {partnerVehicles.map(v => {
                      const plate = v.registration || v.plateNumber || '?';
                      const alreadyDedicated = contract.dedicatedVehicles?.some(dv => dv.vehicleId === v.id || dv.plate === plate);
                      const isAvailable = v.status === 'disponible';
                      return (
                        <div key={v.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${alreadyDedicated ? 'bg-emerald-50/50 border-emerald-200 opacity-60' : 'bg-gray-50 border-gray-200 hover:border-amber-300 hover:bg-amber-50/30'}`} data-testid={`partner-vehicle-${v.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${alreadyDedicated ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                              <Truck className={`w-4 h-4 ${alreadyDedicated ? 'text-emerald-600' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{plate}</p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{v.vehicleType || 'N/A'}</span>
                                {v.capacity && <span className="text-xs text-gray-400">• {v.capacity}T</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs ${isAvailable ? 'text-emerald-600 border-emerald-200' : 'text-orange-600 border-orange-200'}`}>
                              {isAvailable ? 'Disponible' : v.status || 'N/A'}
                            </Badge>
                            {alreadyDedicated ? (
                              <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">Assigné</Badge>
                            ) : (
                              <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={async () => {
                                try {
                                  await axios.patch(`${API}/contracts/${id}/vehicles`, {
                                    vehicleId: v.id, plate, type: v.vehicleType || '', assignedFrom: 'local'
                                  });
                                  toast({ title: "Véhicule assigné", description: plate });
                                  fetchContract();
                                } catch { toast({ title: "Erreur", variant: "destructive" }); }
                              }} data-testid={`assign-vehicle-${v.id}`}>
                                Assigner
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {(!contract.dedicatedVehicles || contract.dedicatedVehicles.length === 0) && partnerVehicles.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun véhicule dédié</p>
                    <p className="text-xs mt-1">Cliquez "Depuis le parc" pour ajouter</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* === DRIVERS PANEL === */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="w-5 h-5 text-gray-400" />
                    Chauffeurs ({contract.dedicatedDrivers?.length || 0})
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => { fetchResources(); setDriverSearch(''); setResourceTab('all'); setShowDriverModal(true); }} data-testid="add-driver-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Depuis le parc
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Assigned drivers */}
                {contract.dedicatedDrivers?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignés au contrat</p>
                    {contract.dedicatedDrivers.map((driver, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`assigned-driver-${index}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{driver.name}</p>
                            <p className="text-xs text-gray-500">{driver.phone}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">Assigné</Badge>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveDriver(driver.driverId)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Partner drivers — Quick assign */}
                {contract.transportPartnerName && partnerDrivers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Chauffeurs de {contract.transportPartnerName}
                    </p>
                    {partnerDrivers.map(d => {
                      const name = `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.name || '?';
                      const alreadyDedicated = contract.dedicatedDrivers?.some(dd => dd.driverId === d.id);
                      const isAvailable = d.status === 'disponible';
                      return (
                        <div key={d.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${alreadyDedicated ? 'bg-emerald-50/50 border-emerald-200 opacity-60' : 'bg-gray-50 border-gray-200 hover:border-amber-300 hover:bg-amber-50/30'}`} data-testid={`partner-driver-${d.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${alreadyDedicated ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                              <User className={`w-4 h-4 ${alreadyDedicated ? 'text-emerald-600' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{name}</p>
                              <p className="text-xs text-gray-400">{d.phone || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs ${isAvailable ? 'text-emerald-600 border-emerald-200' : 'text-orange-600 border-orange-200'}`}>
                              {isAvailable ? 'Disponible' : d.status || 'N/A'}
                            </Badge>
                            {alreadyDedicated ? (
                              <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">Assigné</Badge>
                            ) : (
                              <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={async () => {
                                try {
                                  await axios.patch(`${API}/contracts/${id}/drivers`, {
                                    driverId: d.id, name, phone: d.phone || '', assignedFrom: 'local'
                                  });
                                  const partnerId = contract?.transportPartnerId;
                                  if (partnerId) {
                                    try { await axios.post(`${API}/drivers/${d.id}/assign-partner`, { partnerId, partnerName: contract?.transportPartnerName }); } catch {}
                                  }
                                  toast({ title: "Chauffeur assigné", description: name });
                                  fetchContract();
                                } catch { toast({ title: "Erreur", variant: "destructive" }); }
                              }} data-testid={`assign-driver-${d.id}`}>
                                Assigner
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {(!contract.dedicatedDrivers || contract.dedicatedDrivers.length === 0) && partnerDrivers.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun chauffeur dédié</p>
                    <p className="text-xs mt-1">Cliquez "Depuis le parc" pour ajouter</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Truck Slots Planning */}
          <TruckSlotsConfig contractId={id} contract={contract} onUpdate={fetchContract} />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Financial KPI Summary */}
          {invoices.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="billing-kpi-summary">
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total facturé</p>
                <p className="text-xl font-bold mt-1">{formatPrice(billingKPIs.total)}</p>
                <p className="text-xs text-gray-400 mt-1">{invoices.length} facture{invoices.length > 1 ? 's' : ''}</p>
              </div>
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Encaissé</p>
                <p className="text-xl font-bold text-emerald-600 mt-1">{formatPrice(billingKPIs.paid)}</p>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${billingKPIs.total > 0 ? Math.round((billingKPIs.paid / billingKPIs.total) * 100) : 0}%` }}></div>
                </div>
              </div>
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Reste à encaisser</p>
                <p className={`text-xl font-bold mt-1 ${billingKPIs.unpaid > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{formatPrice(billingKPIs.unpaid)}</p>
              </div>
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">En retard</p>
                <p className={`text-xl font-bold mt-1 ${billingKPIs.overdueCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{billingKPIs.overdueCount}</p>
                {billingKPIs.overdueCount > 0 && <p className="text-xs text-red-500 mt-1">Action requise</p>}
              </div>
            </div>
          )}

          {/* Invoices List - Primary Content */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Factures ({invoices.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchInvoices}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Actualiser
                </Button>
                {contract.status === 'active' && (
                  <Button size="sm" onClick={() => setShowGenerationForm(!showGenerationForm)} data-testid="toggle-generation-form-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Nouvelle facture
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((invoice) => {
                    const total = invoice.montantTotalTTC || 0;
                    const paid = invoice.amountPaid || 0;
                    const due = invoice.amountDue || 0;
                    const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
                    const isOverdue = invoice.status === 'overdue';
                    const isDraft = invoice.status === 'draft';
                    const isPaid = invoice.status === 'paid';
                    const canPay = ['sent', 'partially_paid', 'overdue'].includes(invoice.status);

                    return (
                      <div key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <div className={cn(
                          "flex items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer",
                          isOverdue ? "bg-red-50/50 border-red-200 hover:bg-red-50" : 
                          isPaid ? "bg-emerald-50/50 border-emerald-200 hover:bg-emerald-50" :
                          "bg-white hover:bg-gray-50 border-gray-200"
                        )}>
                          {/* Invoice info */}
                          <div className="flex-1 min-w-0" onClick={() => openInvoiceDetail(invoice)}>
                            <div className="flex items-center gap-3">
                              <p className="font-medium text-sm">{invoice.invoiceNumber}</p>
                              <InvoiceStatusBadge status={invoice.status} />
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {formatDate(invoice.billingPeriod?.start)} → {formatDate(invoice.billingPeriod?.end)}
                              {invoice.dueDate && !isPaid && (
                                <span className={cn("ml-2", isOverdue ? "text-red-500 font-medium" : "text-gray-400")}>
                                  Éch. {formatDate(invoice.dueDate)}
                                </span>
                              )}
                            </p>
                          </div>

                          {/* Payment progress */}
                          <div className="w-40" onClick={() => openInvoiceDetail(invoice)}>
                            {!isDraft && (
                              <>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-500">{formatPrice(paid)}</span>
                                  <span className="font-medium">{formatPrice(total)}</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                  <div className={cn(
                                    "h-2 rounded-full transition-all",
                                    isPaid ? "bg-emerald-500" : isOverdue ? "bg-red-400" : pct > 0 ? "bg-amber-400" : "bg-gray-300"
                                  )} style={{ width: `${pct}%` }}></div>
                                </div>
                                <p className="text-xs text-right mt-0.5 text-gray-400">{pct}%</p>
                              </>
                            )}
                            {isDraft && (
                              <p className="text-sm font-medium text-right">{formatPrice(total)}</p>
                            )}
                          </div>

                          {/* Quick action */}
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {/* Primary action button */}
                            {canPay && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-8 text-xs"
                                onClick={() => {
                                  setInlinePaymentId(inlinePaymentId === invoice.id ? null : invoice.id);
                                  setInlinePaymentForm({ amount: due.toString(), method: '' });
                                }}
                                data-testid={`quick-pay-btn-${invoice.id}`}
                              >
                                <CreditCard className="w-3.5 h-3.5 mr-1" />
                                Payer
                              </Button>
                            )}
                            {isDraft && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-700 border-blue-300 hover:bg-blue-50 h-8 text-xs"
                                onClick={() => handleQuickStatusChange(invoice.id, 'validated')}
                                data-testid={`quick-validate-btn-${invoice.id}`}
                              >
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                Valider
                              </Button>
                            )}
                            {invoice.status === 'validated' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-700 border-purple-300 hover:bg-purple-50 h-8 text-xs"
                                onClick={() => handleQuickStatusChange(invoice.id, 'sent')}
                                data-testid={`quick-send-btn-${invoice.id}`}
                              >
                                <Send className="w-3.5 h-3.5 mr-1" />
                                Envoyer
                              </Button>
                            )}

                            {/* Secondary actions dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`invoice-actions-menu-${invoice.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openInvoiceDetail(invoice)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Détail
                                </DropdownMenuItem>
                                {['validated', 'sent', 'paid', 'partially_paid', 'overdue'].includes(invoice.status) && (
                                  <DropdownMenuItem onClick={() => handleDownloadInvoicePDF(invoice.id, invoice.invoiceNumber, invoice.version)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger PDF
                                  </DropdownMenuItem>
                                )}
                                {(() => {
                                  const fneStatus = invoice.fneCertification?.status;
                                  const isCert = ['certified', 'refunded_partial'].includes(fneStatus);
                                  const isRefundedTotal = ['refunded', 'refunded_total'].includes(fneStatus);
                                  const isFailed = ['failed', 'fne_error'].includes(fneStatus);
                                  const canCertify = ['validated', 'sent', 'paid', 'partially_paid', 'overdue'].includes(invoice.status) && !isCert && !isRefundedTotal;
                                  if (!canCertify && !isCert && !isRefundedTotal) return null;
                                  return (
                                    <>
                                      <DropdownMenuSeparator />
                                      {canCertify && (
                                        <DropdownMenuItem onClick={() => setFneCertifyInvoice(invoice)} className="text-emerald-700" data-testid={`fne-certify-menu-${invoice.id}`}>
                                          <Shield className="w-4 h-4 mr-2" />
                                          {isFailed ? 'Réessayer FNE' : 'Certifier FNE'}
                                        </DropdownMenuItem>
                                      )}
                                      {isCert && (
                                        <>
                                          <DropdownMenuItem onClick={() => window.open(`${API}/fne/pdf/contract/${invoice.id}`, '_blank')} className="text-blue-700" data-testid={`fne-pdf-menu-${invoice.id}`}>
                                            <FileCheck className="w-4 h-4 mr-2" />
                                            PDF FNE certifié
                                          </DropdownMenuItem>
                                          {invoice.fneCertification?.token && (
                                            <DropdownMenuItem onClick={() => window.open(invoice.fneCertification.token, '_blank')} className="text-emerald-700" data-testid={`fne-verify-menu-${invoice.id}`}>
                                              <ExternalLink className="w-4 h-4 mr-2" />
                                              Vérifier DGI
                                            </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem onClick={() => setFneRefundInvoice(invoice)} className="text-rose-600" data-testid={`fne-refund-menu-${invoice.id}`}>
                                            <FileMinus2 className="w-4 h-4 mr-2" />
                                            Avoir FNE
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      {isRefundedTotal && (
                                        <DropdownMenuItem disabled className="text-slate-400" data-testid={`fne-refunded-menu-${invoice.id}`}>
                                          <FileMinus2 className="w-4 h-4 mr-2" />
                                          Avoir total émis
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  );
                                })()}
                                {canPay && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleQuickStatusChange(invoice.id, 'paid')} className="text-emerald-600">
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Marquer payée
                                    </DropdownMenuItem>
                                    {invoice.status !== 'overdue' && (
                                      <DropdownMenuItem onClick={() => handleQuickStatusChange(invoice.id, 'overdue')} className="text-red-600">
                                        <AlertCircle className="w-4 h-4 mr-2" />
                                        Marquer en retard
                                      </DropdownMenuItem>
                                    )}
                                    {invoice.status === 'overdue' && (
                                      <DropdownMenuItem onClick={() => handleQuickStatusChange(invoice.id, 'sent')} className="text-purple-600">
                                        <Send className="w-4 h-4 mr-2" />
                                        Remettre envoyée
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                                {invoice.status === 'validated' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleQuickStatusChange(invoice.id, 'draft')} className="text-gray-600">
                                      <Clock className="w-4 h-4 mr-2" />
                                      Repasser brouillon
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {isDraft && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteInvoice(invoice.id)} className="text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Inline payment form */}
                        {inlinePaymentId === invoice.id && (
                          <div className="ml-4 mr-4 mb-2 mt-1 p-3 bg-emerald-50 border border-emerald-200 rounded-b-lg flex items-end gap-3" data-testid={`inline-payment-form-${invoice.id}`}>
                            <div className="flex-1">
                              <Label className="text-xs text-emerald-700">Montant (FCFA)</Label>
                              <Input
                                type="number"
                                min="0"
                                className="h-8 text-sm"
                                value={inlinePaymentForm.amount}
                                onChange={(e) => setInlinePaymentForm({ ...inlinePaymentForm, amount: e.target.value })}
                                data-testid={`inline-payment-amount-${invoice.id}`}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs text-emerald-700">Méthode</Label>
                              <Select value={inlinePaymentForm.method} onValueChange={(v) => setInlinePaymentForm({ ...inlinePaymentForm, method: v })}>
                                <SelectTrigger className="h-8 text-sm" data-testid={`inline-payment-method-${invoice.id}`}>
                                  <SelectValue placeholder="Choisir..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="virement">Virement</SelectItem>
                                  <SelectItem value="especes">Espèces</SelectItem>
                                  <SelectItem value="cheque">Chèque</SelectItem>
                                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                              disabled={savingPayment || !inlinePaymentForm.amount || !inlinePaymentForm.method}
                              onClick={() => handleInlinePayment(invoice)}
                              data-testid={`inline-payment-confirm-${invoice.id}`}
                            >
                              {savingPayment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                              OK
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setInlinePaymentId(null)}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucune facture générée</p>
                  {contract.status === 'active' && (
                    <Button variant="outline" className="mt-3" onClick={() => setShowGenerationForm(true)} data-testid="empty-state-generate-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Générer la première facture
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collapsible Invoice Generation Form */}
          {contract.status === 'active' && showGenerationForm && (
            <Card className="border-blue-200 bg-blue-50/30" data-testid="invoice-generation-form">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Générer une facture mensuelle
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowGenerationForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Début de période</Label>
                    <Input type="date" value={invoicePeriodStart} onChange={(e) => setInvoicePeriodStart(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Fin de période</Label>
                    <Input type="date" value={invoicePeriodEnd} onChange={(e) => setInvoicePeriodEnd(e.target.value)} />
                  </div>
                </div>
                {/* Prorata Toggle + Billing Preview */}
                {contract.billingRules?.billingMode === 'fleet_location' && invoicePeriodStart && invoicePeriodEnd && (
                  <div className="pt-3 border-t space-y-3" data-testid="billing-preview-section">
                    {/* Toggle prorata */}
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2.5">
                        <CalendarDays className="w-4.5 h-4.5 text-blue-600" />
                        <div>
                          <p className="text-sm font-semibold text-blue-900">Facturer au prorata de la présence</p>
                          <p className="text-xs text-blue-700">Ajuste le montant selon les camions réellement mobilisés</p>
                        </div>
                      </div>
                      <Switch
                        checked={useProrata}
                        onCheckedChange={setUseProrata}
                        data-testid="toggle-prorata"
                      />
                    </div>

                    {/* Preview table - only when prorata is ON */}
                    {useProrata && (
                      <>
                        {loadingPreview ? (
                          <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Calcul de la présence réelle...
                          </div>
                        ) : billingPreview && billingPreview.hasAttendanceData ? (
                          <div className="space-y-2">
                            <div className="bg-gray-50 border rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-gray-100/50">
                                    <th className="text-left px-3 py-2 font-medium text-xs text-gray-500">Slot</th>
                                    <th className="text-center px-3 py-2 font-medium text-xs text-gray-500">Présence</th>
                                    <th className="text-right px-3 py-2 font-medium text-xs text-gray-500">Tarif/mois</th>
                                    <th className="text-right px-3 py-2 font-medium text-xs text-gray-500">Prorata</th>
                                    <th className="text-right px-3 py-2 font-medium text-xs text-gray-500">Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {billingPreview.slots.map((slot) => (
                                    <tr key={slot.slotId} className="border-b border-gray-100 last:border-0">
                                      <td className="px-3 py-2">
                                        <span className="font-medium text-gray-800">{slot.label}</span>
                                        <span className="text-xs text-gray-400 ml-1.5">{slot.vehicleTypeLabel}</span>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <span className={`font-mono font-semibold ${slot.billableDays === slot.expectedDays ? 'text-green-700' : slot.billableDays === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                          {slot.billableDays}/{slot.expectedDays}
                                        </span>
                                        <span className="text-xs text-gray-400 ml-1">jours</span>
                                      </td>
                                      <td className="px-3 py-2 text-right font-mono text-gray-600">
                                        {slot.monthlyRate?.toLocaleString('fr-FR')}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${slot.prorataRate >= 1 ? 'bg-green-100 text-green-700' : slot.prorataRate === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                          {Math.round(slot.prorataRate * 100)}%
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">
                                        {slot.proratedAmount?.toLocaleString('fr-FR')}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                                    <td colSpan={4} className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                      Total présence
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono font-bold text-blue-700">
                                      {billingPreview.attendanceTotal?.toLocaleString('fr-FR')} FCFA
                                    </td>
                                  </tr>
                                  {billingPreview.difference > 0 && (
                                    <tr className="bg-amber-50/50">
                                      <td colSpan={4} className="px-3 py-1.5 text-right text-xs text-amber-700">
                                        vs contractuel ({billingPreview.contractualTotal?.toLocaleString('fr-FR')} FCFA)
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono text-xs text-amber-700 font-semibold">
                                        -{billingPreview.difference?.toLocaleString('fr-FR')}
                                      </td>
                                    </tr>
                                  )}
                                </tfoot>
                              </table>
                            </div>
                            <p className="text-xs text-gray-500">
                              Calcul basé sur les véhicules uniques mobilisés par jour. Vérifiez l'onglet Présence avant de valider.
                            </p>
                          </div>
                        ) : billingPreview && !billingPreview.hasAttendanceData ? (
                          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            Aucune donnée de présence pour cette période. Désactivez le prorata ou vérifiez le calendrier.
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4 pt-3 border-t">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">TVA</p>
                        <p className="text-xs text-gray-500">{contract.billingRules?.tauxTVA || 18}%</p>
                      </div>
                    </div>
                    <Switch checked={invoiceApplyTVA} onCheckedChange={setInvoiceApplyTVA} />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">Détail opérations</p>
                        <p className="text-xs text-gray-500">Afficher chaque opération</p>
                      </div>
                    </div>
                    <Switch checked={invoiceShowDetails} onCheckedChange={setInvoiceShowDetails} />
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <Label className="text-sm font-medium">Délai de paiement</Label>
                    <Select value={invoicePaymentTerms.toString()} onValueChange={(v) => setInvoicePaymentTerms(parseInt(v))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Immédiat</SelectItem>
                        <SelectItem value="7">7 jours</SelectItem>
                        <SelectItem value="15">15 jours</SelectItem>
                        <SelectItem value="30">30 jours</SelectItem>
                        <SelectItem value="45">45 jours</SelectItem>
                        <SelectItem value="60">60 jours</SelectItem>
                        <SelectItem value="90">90 jours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Taxe globale cachée 2% — invisible au client (héritée du contrat, modifiable ici) */}
                <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid="invoice-hidden-tax-block">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-violet-500" />
                    <div>
                      <p className="text-sm font-medium text-violet-900">Taxe cachée 2%</p>
                      <p className="text-xs text-violet-600">Incorporée dans les prix (arrondi 100 F). Invisible pour le client.</p>
                    </div>
                  </div>
                  <Switch checked={invoiceApplyHiddenTax} onCheckedChange={setInvoiceApplyHiddenTax} data-testid="invoice-apply-hidden-tax-switch" />
                </div>
                <div className="pt-3 border-t">
                  {/* Fix P2 : édition des tarifs par slot AVANT génération (fleet_location non-prorata uniquement) */}
                  {contract?.billingRules?.billingMode === 'fleet_location'
                    && !useProrata
                    && Array.isArray(contract?.truckSlots)
                    && contract.truckSlots.length > 0 && (
                    <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">
                          Tarif par camion <span className="text-xs text-gray-500 font-normal">(modifiable avant génération)</span>
                        </Label>
                        {Object.keys(slotRateOverrides).length > 0 && (
                          <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-blue-600 underline"
                            onClick={() => setSlotRateOverrides({})}
                            data-testid="reset-slot-rates-btn"
                          >
                            Réinitialiser
                          </button>
                        )}
                      </div>
                      <div className="divide-y">
                        {contract.truckSlots.map((slot) => {
                          const defaultRate = slot.monthlyRate || 0;
                          const currentRate = slotRateOverrides[slot.slotId] !== undefined
                            ? slotRateOverrides[slot.slotId]
                            : defaultRate;
                          const isOverridden = slotRateOverrides[slot.slotId] !== undefined
                            && Number(slotRateOverrides[slot.slotId]) !== defaultRate;
                          return (
                            <div key={slot.slotId} className="flex items-center justify-between py-2 gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {slot.vehicleTypeLabel || slot.vehicleType || 'Camion'} · {slot.label || 'Poste'}
                                </p>
                                {(slot.assignedVehiclePlates?.length > 0 || slot.assignedPlates?.length > 0) && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {(slot.assignedVehiclePlates || slot.assignedPlates || []).join(', ')}
                                  </p>
                                )}
                              </div>
                              <div className="relative w-40">
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className={`pr-14 text-right ${isOverridden ? 'border-amber-400 bg-amber-50' : ''}`}
                                  value={currentRate}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setSlotRateOverrides((prev) => {
                                      const next = { ...prev };
                                      if (val === '' || val === null) {
                                        delete next[slot.slotId];
                                      } else {
                                        next[slot.slotId] = parseFloat(val) || 0;
                                      }
                                      return next;
                                    });
                                  }}
                                  data-testid={`slot-rate-input-${slot.slotId}`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 font-medium">
                                  FCFA
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-2 pt-2 border-t flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total tarif par camion :</span>
                        <span className="font-mono font-semibold text-blue-700" data-testid="slots-total-preview">
                          {contract.truckSlots.reduce((sum, s) => {
                            const rate = slotRateOverrides[s.slotId] !== undefined
                              ? Number(slotRateOverrides[s.slotId]) || 0
                              : (s.monthlyRate || 0);
                            return sum + rate;
                          }, 0).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    </div>
                  )}
                  <Label className="text-sm font-medium">Montant à facturer HT (optionnel)</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Laissez vide pour le calcul automatique. Renseignez un montant pour une réduction ou prorata.
                  </p>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="pr-14"
                      placeholder="Calcul auto si vide"
                      value={invoiceCustomAmount}
                      onChange={(e) => setInvoiceCustomAmount(e.target.value)}
                      data-testid="invoice-custom-amount-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">XOF</span>
                  </div>
                  {invoiceCustomAmount && (
                    <div className="flex items-center gap-1.5 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" /></svg>
                      <span>Montant personnalisé : <strong>{parseFloat(invoiceCustomAmount).toLocaleString('fr-FR')} XOF HT</strong> sera utilisé au lieu du calcul automatique</span>
                    </div>
                  )}
                  {invoiceCustomAmount && (
                    <div className="mt-3 space-y-2 p-3 bg-indigo-50/50 border border-indigo-200 rounded">
                      <Label className="text-xs font-semibold text-indigo-900">Motif du montant personnalisé *</Label>
                      <Select value={invoiceCustomAmountReason} onValueChange={setInvoiceCustomAmountReason}>
                        <SelectTrigger className="h-8 text-xs bg-white" data-testid="invoice-custom-reason-select">
                          <SelectValue placeholder="Sélectionner un motif…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="commercial_gesture">Geste commercial / Remise</SelectItem>
                          <SelectItem value="error_correction">Correction d'erreur</SelectItem>
                          <SelectItem value="partial_billing">Facturation partielle</SelectItem>
                          <SelectItem value="negotiated">Montant négocié</SelectItem>
                          <SelectItem value="other">Autre (précision requise)</SelectItem>
                        </SelectContent>
                      </Select>
                      {invoiceCustomAmountReason === 'other' && (
                        <Input
                          placeholder="Précisez le motif (obligatoire pour 'Autre')…"
                          value={invoiceCustomAmountNote}
                          onChange={(e) => setInvoiceCustomAmountNote(e.target.value)}
                          className="h-8 text-xs bg-white"
                          data-testid="invoice-custom-note-input"
                        />
                      )}
                      {invoiceCustomAmountReason && invoiceCustomAmountReason !== 'other' && (
                        <Input
                          placeholder="Note libre (optionnelle)…"
                          value={invoiceCustomAmountNote}
                          onChange={(e) => setInvoiceCustomAmountNote(e.target.value)}
                          className="h-8 text-xs bg-white"
                        />
                      )}
                      <p className="text-[10px] text-indigo-700 italic">
                        Tracé en audit pour le contrôle financier. L'écart apparaîtra dans le dashboard Recouvrement.
                      </p>
                    </div>
                  )}
                </div>
                <div className="pt-3 border-t">
                  <Label className="text-sm font-medium">Notes (optionnel)</Label>
                  <Textarea className="mt-1" placeholder="Clauses particulières..." value={invoiceCustomNotes} onChange={(e) => setInvoiceCustomNotes(e.target.value)} rows={2} />
                </div>
                <div className="flex justify-end pt-3">
                  <Button onClick={handleGenerateInvoice} disabled={generatingInvoice || !invoicePeriodStart || !invoicePeriodEnd || (invoiceCustomAmount && !invoiceCustomAmountReason) || (invoiceCustomAmountReason === 'other' && !invoiceCustomAmountNote.trim())} className="bg-blue-600 hover:bg-blue-700" data-testid="generate-invoice-btn">
                    {generatingInvoice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Générer la facture
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Billing Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-400" />
                Règles de Facturation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Mode de facturation</p>
                  <p className="font-medium">
                    {contract.billingRules?.billingMode === 'monthly_fixed' 
                      ? 'Forfait mensuel fixe' 
                      : contract.billingRules?.billingMode === 'fleet_location'
                      ? 'Location de flotte'
                      : contract.billingRules?.billingMode === 'per_tonnage'
                      ? 'Facturation au tonnage'
                      : 'Facturation à l\'unité'}
                  </p>
                </div>
                
                {contract.billingRules?.billingMode === 'monthly_fixed' ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">Prix mensuel</p>
                      <p className="font-medium text-lg">{formatPrice(contract.billingRules?.monthlyFixedPrice)}</p>
                    </div>
                    {contract.billingRules?.monthlyFixedDescription && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="font-medium">{contract.billingRules?.monthlyFixedDescription}</p>
                      </div>
                    )}
                  </>
                ) : contract.billingRules?.billingMode === 'fleet_location' ? (
                  <div className="md:col-span-1">
                    <p className="text-sm text-gray-500">Redevance mensuelle totale</p>
                    <p className="font-medium text-lg text-blue-700">{formatPrice(contract.billingRules?.totalMonthlyFleetAmount)}</p>
                  </div>
                ) : contract.billingRules?.billingMode === 'per_tonnage' ? (
                  <div>
                    <p className="text-sm text-gray-500">Prix par tonne</p>
                    <p className="font-medium text-lg">{formatPrice(contract.billingRules?.pricePerTon)}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500">Prix par {contract.terminology}</p>
                    <p className="font-medium text-lg">{formatPrice(contract.billingRules?.pricePerUnit)}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-sm text-gray-500">TVA</p>
                  <p className="font-medium">
                    {contract.billingRules?.applyTVA 
                      ? `Oui (${contract.billingRules?.tauxTVA || 18}%)`
                      : 'Non applicable'}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Politique carburant</p>
                  <p className="font-medium capitalize">
                    {contract.billingRules?.fuelPolicy === 'included' && 'Inclus'}
                    {contract.billingRules?.fuelPolicy === 'excluded' && 'Exclu'}
                    {contract.billingRules?.fuelPolicy === 'advance_with_receipts' && 'Avance avec justificatifs'}
                  </p>
                </div>
              </div>
              
              <div className="pt-4 border-t" data-testid="contract-charge-matrix-properties">
                <p className="text-sm text-gray-500 mb-2">Prise en charge des charges</p>
                <div className="flex flex-wrap gap-2">
                  {CHARGE_POSTES.map((p) => {
                    const bearer = p.key === 'truck_rental'
                      ? 'baobab'
                      : (contract.chargeMatrix?.[p.key]
                         || (p.key === 'fuel'
                             ? (contract.billingRules?.fuelPolicy === 'excluded' ? 'client' : 'baobab')
                             : (p.key === 'tolls' || p.key === 'driver' ? 'baobab' : 'na')));
                    const tone = bearer === 'baobab'
                      ? 'border-gray-300 text-gray-900 bg-white'
                      : bearer === 'client'
                        ? 'border-sky-200 text-sky-700 bg-sky-50'
                        : bearer === 'included_carrier'
                          ? 'border-violet-200 text-violet-700 bg-violet-50'
                          : 'border-gray-200 text-gray-400 bg-gray-50';
                    return (
                      <span key={p.key} className={`text-xs px-2.5 py-1 rounded-full border ${tone}`}
                            data-testid={`property-matrix-${p.key}`}>
                        {p.label} · {BEARER_LABELS[bearer] || 'Non concerné'}
                      </span>
                    );
                  })}
                </div>
                {!contract.chargeMatrix && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    Déduite des règles de facturation — à confirmer dans l'onglet Rentabilité.
                  </p>
                )}
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Options d'affichage facture</p>
                <p className="font-medium">
                  {contract.invoiceDisplayOptions?.showDetailedLines 
                    ? 'Détail de chaque opération'
                    : 'Ligne groupée (par défaut)'}
                </p>
              </div>
              
              {/* Auto-generation settings */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Génération automatique de factures</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm">Activation</p>
                    <p className={`font-medium ${contract.billingRules?.autoGenerateInvoice ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {contract.billingRules?.autoGenerateInvoice ? 'Activée' : 'Désactivée'}
                    </p>
                  </div>
                  {contract.billingRules?.autoGenerateInvoice && (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm">Jour de génération</p>
                        <p className="font-medium">Le {contract.billingRules?.autoGenerateDay || 1} de chaque mois</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm">Envoi par email</p>
                        <p className={`font-medium ${contract.billingRules?.sendInvoiceByEmail ? 'text-emerald-600' : 'text-gray-500'}`}>
                          {contract.billingRules?.sendInvoiceByEmail !== false ? 'Oui' : 'Non'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─── Fleet Section (fleet_location mode only) ─── */}
          {contract.billingRules?.billingMode === 'fleet_location' && (
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Truck className="w-5 h-5" />
                    Flotte mobilisée
                    <span className="ml-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {(contract.billingRules?.fleetItems || []).reduce((s, i) => s + (i.quantity || 0), 0)} camion{(contract.billingRules?.fleetItems || []).reduce((s, i) => s + (i.quantity || 0), 0) > 1 ? 's' : ''}
                    </span>
                  </CardTitle>
                  {hasPermission('contracts.edit') ? (
                    <Button size="sm" variant="outline" onClick={() => {
                      const current = contract.billingRules?.fleetItems || [];
                      setFleetItems(current.length > 0 ? JSON.parse(JSON.stringify(current)) : [
                        { vehicleType: '', vehicleTypeLabel: '', vehicleTypeIcon: '🚛', quantity: 1, monthlyRatePerTruck: 0 }
                      ]);
                      setFleetAmendmentDate(new Date().toISOString().split('T')[0]);
                      setFleetAmendmentReason('');
                      setShowFleetModal(true);
                    }} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      Modifier la flotte
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent>
                {(contract.billingRules?.fleetItems || []).length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucun type de camion défini.</p>
                ) : (
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-3">
                      <div className="col-span-5">Type de camion</div>
                      <div className="col-span-2 text-center">Qté</div>
                      <div className="col-span-3 text-right">Tarif/camion</div>
                      <div className="col-span-2 text-right">Sous-total</div>
                    </div>
                    {/* Rows */}
                    {(contract.billingRules?.fleetItems || []).map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-blue-50 rounded-lg px-3 py-2.5">
                        <div className="col-span-5 font-medium text-gray-800 flex items-center gap-1.5">
                          <span>{item.vehicleTypeLabel || item.vehicleType}</span>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="bg-blue-200 text-blue-900 font-bold text-sm px-2 py-0.5 rounded-full">×{item.quantity}</span>
                        </div>
                        <div className="col-span-3 text-right text-sm text-gray-600">
                          {new Intl.NumberFormat('fr-FR').format(item.monthlyRatePerTruck)} FCFA
                        </div>
                        <div className="col-span-2 text-right text-sm font-semibold text-blue-800">
                          {new Intl.NumberFormat('fr-FR').format((item.quantity || 0) * (item.monthlyRatePerTruck || 0))}
                        </div>
                      </div>
                    ))}
                    {/* Total */}
                    <div className="flex items-center justify-between pt-3 border-t border-blue-200 px-3">
                      <p className="text-sm font-medium text-gray-600">Redevance mensuelle totale</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {new Intl.NumberFormat('fr-FR').format(contract.billingRules?.totalMonthlyFleetAmount || 0)}
                        <span className="text-sm font-normal text-gray-500 ml-1">FCFA/mois</span>
                      </p>
                    </div>
                    {/* Amendment count */}
                    {contract.fleetAmendments?.length > 0 && (
                      <p className="text-xs text-blue-600 text-center pt-1 cursor-pointer hover:underline" onClick={() => setActiveTab('history')}>
                        {contract.fleetAmendments.length} avenant{contract.fleetAmendments.length > 1 ? 's' : ''} enregistré{contract.fleetAmendments.length > 1 ? 's' : ''} → voir l'historique
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </TabsContent>

        {/* Attendance/Presence Tab */}
        <TabsContent value="attendance" className="space-y-6">
          <AttendanceCalendar contractId={id} contract={contract} />
        </TabsContent>

        {/* Quotes Tab — PM Spec 29/04/2026 */}
        <TabsContent value="quotes" className="space-y-6">
          <ContractQuoteHistoryTab contract={contract} />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <ContractAnalyticsTab contractId={id} />
        </TabsContent>

        {/* Rentabilité — PM Spec 21/06/2026 (matrice de prise en charge + marge brute) */}
        <TabsContent value="margin" className="space-y-6">
          <ContractProfitabilityTab contractId={id} contract={contract} />
        </TabsContent>

        {/* Préfinancement des charges (client ou Baobab) */}
        <TabsContent value="prefinancing" className="space-y-6">
          <ContractPrefinancingTab contractId={id} />
        </TabsContent>

        {/* Compliance Tab - Staff only */}
        <TabsContent value="compliance" className="space-y-6">
          <ComplianceTab contractId={id} />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          {/* Fleet Amendments history */}
          {contract.billingRules?.billingMode === 'fleet_location' && contract.fleetAmendments?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  Avenants de Flotte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...contract.fleetAmendments].reverse().map((amendment, idx) => (
                    <div key={idx} className="border border-blue-100 rounded-lg p-4 bg-blue-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-blue-900">{amendment.reason}</p>
                          <p className="text-xs text-blue-600 mt-0.5">
                            Effectif le {new Date(amendment.effectiveDate).toLocaleDateString('fr-FR')} · 
                            Créé par {amendment.createdBy} · 
                            {new Date(amendment.createdAt).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-gray-500 line-through">{new Intl.NumberFormat('fr-FR').format(amendment.previousTotalMonthly)} FCFA</p>
                          <p className="text-blue-700 font-bold">{new Intl.NumberFormat('fr-FR').format(amendment.newTotalMonthly)} FCFA/mois</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="text-gray-500 mb-1">Avant</p>
                          {amendment.previousFleet?.map((item, i) => (
                            <p key={i} className="text-gray-600">{item.vehicleTypeLabel} × {item.quantity} ({new Intl.NumberFormat('fr-FR').format(item.monthlyRatePerTruck)} FCFA)</p>
                          ))}
                        </div>
                        <div>
                          <p className="text-gray-500 mb-1">Après</p>
                          {amendment.newFleet?.map((item, i) => (
                            <p key={i} className="text-blue-800 font-medium">{item.vehicleTypeLabel} × {item.quantity} ({new Intl.NumberFormat('fr-FR').format(item.monthlyRatePerTruck)} FCFA)</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Historique des Statuts</CardTitle>
            </CardHeader>
            <CardContent>
              {contract.statusHistory?.length > 0 ? (
                <div className="space-y-4">
                  {contract.statusHistory.slice().reverse().map((entry, index) => {
                    const config = CONTRACT_STATUS_CONFIG[entry.status] || CONTRACT_STATUS_CONFIG.draft;
                    const Icon = config.icon;
                    
                    return (
                      <div key={index} className="flex items-start gap-4">
                        <div className={cn('p-2 rounded-lg', config.color)}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{config.label}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(entry.date)} {entry.date && new Date(entry.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {entry.reason && (
                            <p className="text-sm text-gray-600 mt-1">{entry.reason}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-6">Aucun historique</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="space-y-6">
          <InternalNotesTab
            entityType="contract"
            entityId={contract.id}
            currentUser={user}
            onCountChange={setContractNotesCount}
          />
        </TabsContent>
      </Tabs>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uploader le Contrat Signé</DialogTitle>
            <DialogDescription>
              Le contrat signé doit être au format PDF
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Fichier PDF *</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
            
            <div>
              <Label>Date de signature</Label>
              <Input
                type="date"
                value={uploadSignedDate}
                onChange={(e) => setUploadSignedDate(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Signataires (séparés par des virgules)</Label>
              <Input
                value={uploadSignatories}
                onChange={(e) => setUploadSignatories(e.target.value)}
                placeholder="M. Koné (Client), M. Bamba (Baobab)"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le Statut</DialogTitle>
            <DialogDescription>
              {newStatus === 'active' && 'Activer ce contrat pour permettre la création d\'opérations'}
              {newStatus === 'suspended' && 'Suspendre temporairement ce contrat'}
              {newStatus === 'closed' && 'Clôturer définitivement ce contrat'}
              {newStatus === 'restore' && contract?.status === 'closed' && 'Restaurer ce contrat clôturé — il redeviendra actif.'}
              {newStatus === 'restore' && contract?.status === 'cancelled' && 'Restaurer ce contrat annulé — il repassera en brouillon.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{newStatus === 'restore' ? 'Motif de restauration (obligatoire)' : 'Motif (optionnel)'}</Label>
              <Input
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder={newStatus === 'restore' ? 'Ex: Clôture par erreur...' : 'Raison du changement de statut...'}
              />
            </div>
            {newStatus === 'restore' && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Cette action sera enregistrée dans l'historique du contrat.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleStatusChange} 
              disabled={changingStatus || (newStatus === 'restore' && !statusReason)}
              className={cn(
                newStatus === 'active' && 'bg-green-600 hover:bg-green-700',
                newStatus === 'suspended' && 'bg-amber-600 hover:bg-amber-700',
                newStatus === 'closed' && 'bg-gray-600 hover:bg-gray-700',
                newStatus === 'restore' && 'bg-blue-600 hover:bg-blue-700'
              )}
            >
              {changingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vehicle Modal */}
      <Dialog open={showVehicleModal} onOpenChange={(open) => { setShowVehicleModal(open); if (!open) setVehicleSearch(''); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#1db584]" />
              Ajouter un Véhicule
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={resourceTab} onValueChange={(v) => { setResourceTab(v); setVehicleSearch(''); }} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" data-testid="vehicle-tab-all">Tous ({availableVehicles.length + truckDbVehicles.length})</TabsTrigger>
              <TabsTrigger value="local" data-testid="vehicle-tab-local">Locaux ({availableVehicles.length})</TabsTrigger>
              <TabsTrigger value="truckdb" data-testid="vehicle-tab-truckdb">Truck DB ({truckDbVehicles.length})</TabsTrigger>
              <TabsTrigger value="manual" data-testid="vehicle-tab-manual">Saisie manuelle</TabsTrigger>
            </TabsList>

            {/* Search bar for list tabs */}
            {resourceTab !== 'manual' && (
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par plaque ou type..."
                  value={vehicleSearch}
                  onChange={(e) => setVehicleSearch(e.target.value)}
                  className="pl-9"
                  data-testid="vehicle-search-input"
                />
                {vehicleSearch && (
                  <button onClick={() => setVehicleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* "Tous" tab - unified view */}
            <TabsContent value="all" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                const q = vehicleSearch.toLowerCase().trim();
                const localFiltered = availableVehicles.filter(v => {
                  const plate = (v.plateNumber || v.registration || v.plate || '').toLowerCase();
                  const type = (v.vehicleType || v.type || '').toLowerCase();
                  return !q || plate.includes(q) || type.includes(q);
                }).map(v => ({ ...v, _source: 'local' }));
                const truckFiltered = truckDbVehicles.filter(v => {
                  const plate = (v.immatriculation || v.registration || v.plate || '').toLowerCase();
                  const type = (v.type_camion || v.type || '').toLowerCase();
                  return !q || plate.includes(q) || type.includes(q);
                }).map(v => ({ ...v, _source: 'truckdb' }));
                const all = [...localFiltered, ...truckFiltered];
                if (loadingTruckDb) return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
                if (all.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun véhicule trouvé pour "{vehicleSearch}"</p>
                    <Button variant="outline" size="sm" onClick={() => { setVehicleForm({ vehicleId: '', plate: vehicleSearch, type: '', assignedFrom: new Date().toISOString().split('T')[0] }); setResourceTab('manual'); setVehicleSearch(''); }} data-testid="vehicle-create-from-search">
                      <Plus className="w-3 h-3 mr-1" /> Créer "{vehicleSearch}" manuellement
                    </Button>
                  </div>
                );
                if (all.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Aucun véhicule disponible</p>;
                return all.map((v, idx) => {
                  const isLocal = v._source === 'local';
                  const plate = isLocal ? (v.plateNumber || v.registration || v.plate) : (v.immatriculation || v.registration || v.plate);
                  const type = isLocal ? (v.vehicleType || v.type) : (v.type_camion || v.type);
                  return (
                    <div key={v.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`vehicle-item-${v.id || idx}`}
                      onClick={() => {
                        setVehicleForm({
                          vehicleId: isLocal ? v.id : `truckdb-${v.id || idx}`,
                          plate: plate || '',
                          type: type || 'plateau',
                          assignedFrom: new Date().toISOString().split('T')[0]
                        });
                        setResourceTab('manual');
                        setVehicleSearch('');
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Truck className={cn("w-4 h-4", isLocal ? "text-gray-400" : "text-blue-400")} />
                        <div>
                          <p className="font-medium text-sm">{plate}</p>
                          <p className="text-xs text-gray-500">{type} {!isLocal && v.chauffeur_nom ? `• ${v.chauffeur_nom}` : ''}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn("text-xs", isLocal ? "" : "text-blue-600 border-blue-200")}>{isLocal ? 'Local' : 'Truck DB'}</Badge>
                    </div>
                  );
                });
              })()}
            </TabsContent>

            {/* Local vehicles */}
            <TabsContent value="local" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                const q = vehicleSearch.toLowerCase().trim();
                const filtered = availableVehicles.filter(v => {
                  const plate = (v.plateNumber || v.registration || v.plate || '').toLowerCase();
                  const type = (v.vehicleType || v.type || '').toLowerCase();
                  return !q || plate.includes(q) || type.includes(q);
                });
                if (filtered.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun véhicule local pour "{vehicleSearch}"</p>
                    <Button variant="outline" size="sm" onClick={() => { setVehicleForm({ vehicleId: '', plate: vehicleSearch, type: '', assignedFrom: new Date().toISOString().split('T')[0] }); setResourceTab('manual'); setVehicleSearch(''); }} data-testid="vehicle-local-create-from-search">
                      <Plus className="w-3 h-3 mr-1" /> Créer "{vehicleSearch}" manuellement
                    </Button>
                  </div>
                );
                if (filtered.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Aucun véhicule local</p>;
                return filtered.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setVehicleForm({ vehicleId: v.id, plate: v.plateNumber || v.registration || v.plate, type: v.vehicleType || v.type, assignedFrom: new Date().toISOString().split('T')[0] });
                      setResourceTab('manual');
                      setVehicleSearch('');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Truck className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">{v.plateNumber || v.registration || v.plate}</p>
                        <p className="text-xs text-gray-500">{formatVehicleType(v.vehicleType || v.type)}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">Local</Badge>
                  </div>
                ));
              })()}
            </TabsContent>

            {/* Truck DB vehicles */}
            <TabsContent value="truckdb" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                if (loadingTruckDb) return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
                const q = vehicleSearch.toLowerCase().trim();
                const filtered = truckDbVehicles.filter(v => {
                  const plate = (v.immatriculation || v.registration || v.plate || '').toLowerCase();
                  const type = (v.type_camion || v.type || '').toLowerCase();
                  return !q || plate.includes(q) || type.includes(q);
                });
                if (filtered.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun véhicule Truck DB pour "{vehicleSearch}"</p>
                  </div>
                );
                if (filtered.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Truck DB non disponible ou aucun véhicule</p>;
                return filtered.map((v, idx) => (
                  <div key={v.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setVehicleForm({ vehicleId: `truckdb-${v.id || idx}`, plate: v.immatriculation || v.registration || v.plate || '', type: v.type_camion || v.type || 'plateau', assignedFrom: new Date().toISOString().split('T')[0] });
                      setResourceTab('manual');
                      setVehicleSearch('');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Truck className="w-4 h-4 text-blue-400" />
                      <div>
                        <p className="font-medium text-sm">{v.immatriculation || v.registration || v.plate}</p>
                        <p className="text-xs text-gray-500">{v.type_camion || v.type} {v.chauffeur_nom ? `• ${v.chauffeur_nom}` : ''}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">Truck DB</Badge>
                  </div>
                ));
              })()}
            </TabsContent>

            {/* Manual entry */}
            <TabsContent value="manual" className="space-y-4">
              <div>
                <Label>Immatriculation *</Label>
                <Input
                  value={vehicleForm.plate}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, plate: e.target.value })}
                  placeholder="1234 AB 01"
                  data-testid="vehicle-plate-input"
                />
              </div>
              
              <div>
                <Label>Type *</Label>
                <Select 
                  value={vehicleForm.type}
                  onValueChange={(value) => setVehicleForm({ ...vehicleForm, type: value })}
                >
                  <SelectTrigger data-testid="vehicle-type-select">
                    <SelectValue placeholder="Type de véhicule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plateau">Plateau</SelectItem>
                    <SelectItem value="benne">Benne</SelectItem>
                    <SelectItem value="citerne">Citerne</SelectItem>
                    <SelectItem value="frigorifique">Frigorifique</SelectItem>
                    <SelectItem value="porte_conteneur">Porte-conteneur</SelectItem>
                    <SelectItem value="fourgon">Fourgon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Assigné depuis *</Label>
                <Input
                  type="date"
                  value={vehicleForm.assignedFrom}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, assignedFrom: e.target.value })}
                  data-testid="vehicle-date-input"
                />
              </div>
              {vehicleForm.vehicleId && (
                <p className="text-xs text-[#1db584]">Véhicule pré-rempli depuis la sélection</p>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVehicleModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddVehicle} disabled={savingResource} className="bg-[#1db584] hover:bg-[#19a074]" data-testid="add-vehicle-submit">
              {savingResource ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Driver Modal */}
      <Dialog open={showDriverModal} onOpenChange={(open) => { setShowDriverModal(open); if (!open) setDriverSearch(''); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1db584]" />
              Ajouter un Chauffeur
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={resourceTab} onValueChange={(v) => { setResourceTab(v); setDriverSearch(''); }} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" data-testid="driver-tab-all">Tous ({availableDrivers.length + truckDbDrivers.length})</TabsTrigger>
              <TabsTrigger value="local" data-testid="driver-tab-local">Locaux ({availableDrivers.length})</TabsTrigger>
              <TabsTrigger value="truckdb" data-testid="driver-tab-truckdb">Truck DB ({truckDbDrivers.length})</TabsTrigger>
              <TabsTrigger value="manual" data-testid="driver-tab-manual">Saisie manuelle</TabsTrigger>
            </TabsList>

            {/* Search bar for list tabs */}
            {resourceTab !== 'manual' && (
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom ou téléphone..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  className="pl-9"
                  data-testid="driver-search-input"
                />
                {driverSearch && (
                  <button onClick={() => setDriverSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* "Tous" tab - unified view */}
            <TabsContent value="all" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                const q = driverSearch.toLowerCase().trim();
                const localFiltered = availableDrivers.filter(d => {
                  const name = `${d.firstName || ''} ${d.lastName || d.name || ''}`.toLowerCase();
                  const phone = (d.phone || d.telephone || '').toLowerCase();
                  return !q || name.includes(q) || phone.includes(q);
                }).map(d => ({ ...d, _source: 'local' }));
                const truckFiltered = truckDbDrivers.filter(d => {
                  const name = (d.nom || d.name || `${d.prenom || ''} ${d.nom_famille || ''}`).toLowerCase();
                  const phone = (d.telephone || d.phone || '').toLowerCase();
                  return !q || name.includes(q) || phone.includes(q);
                }).map(d => ({ ...d, _source: 'truckdb' }));
                const all = [...localFiltered, ...truckFiltered];
                if (loadingTruckDb) return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
                if (all.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun chauffeur trouvé pour "{driverSearch}"</p>
                    <Button variant="outline" size="sm" onClick={() => { setDriverForm({ driverId: '', name: driverSearch, phone: '', assignedFrom: new Date().toISOString().split('T')[0] }); setResourceTab('manual'); setDriverSearch(''); }} data-testid="driver-create-from-search">
                      <Plus className="w-3 h-3 mr-1" /> Créer "{driverSearch}" manuellement
                    </Button>
                  </div>
                );
                if (all.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Aucun chauffeur disponible</p>;
                return all.map((d, idx) => {
                  const isLocal = d._source === 'local';
                  const name = isLocal ? `${d.firstName || ''} ${d.lastName || d.name || ''}`.trim() : (d.nom || d.name || `${d.prenom || ''} ${d.nom_famille || ''}`.trim());
                  const phone = isLocal ? (d.phone || d.telephone || '') : (d.telephone || d.phone || '');
                  return (
                    <div key={d.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`driver-item-${d.id || idx}`}
                      onClick={() => {
                        setDriverForm({
                          driverId: isLocal ? d.id : `truckdb-${d.id || idx}`,
                          name: name,
                          phone: phone,
                          assignedFrom: new Date().toISOString().split('T')[0]
                        });
                        setResourceTab('manual');
                        setDriverSearch('');
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <User className={cn("w-4 h-4", isLocal ? "text-gray-400" : "text-blue-400")} />
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          <p className="text-xs text-gray-500">{phone} {!isLocal && d.disponible ? '• Disponible' : ''}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn("text-xs", isLocal ? "" : "text-blue-600 border-blue-200")}>{isLocal ? 'Local' : 'Truck DB'}</Badge>
                    </div>
                  );
                });
              })()}
            </TabsContent>

            {/* Local drivers */}
            <TabsContent value="local" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                const q = driverSearch.toLowerCase().trim();
                const filtered = availableDrivers.filter(d => {
                  const name = `${d.firstName || ''} ${d.lastName || d.name || ''}`.toLowerCase();
                  const phone = (d.phone || d.telephone || '').toLowerCase();
                  return !q || name.includes(q) || phone.includes(q);
                });
                if (filtered.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun chauffeur local pour "{driverSearch}"</p>
                    <Button variant="outline" size="sm" onClick={() => { setDriverForm({ driverId: '', name: driverSearch, phone: '', assignedFrom: new Date().toISOString().split('T')[0] }); setResourceTab('manual'); setDriverSearch(''); }} data-testid="driver-local-create-from-search">
                      <Plus className="w-3 h-3 mr-1" /> Créer "{driverSearch}" manuellement
                    </Button>
                  </div>
                );
                if (filtered.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Aucun chauffeur local</p>;
                return filtered.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setDriverForm({ driverId: d.id, name: `${d.firstName || ''} ${d.lastName || d.name || ''}`.trim(), phone: d.phone || d.telephone || '', assignedFrom: new Date().toISOString().split('T')[0] });
                      setResourceTab('manual');
                      setDriverSearch('');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">{d.firstName || ''} {d.lastName || d.name || ''}</p>
                        <p className="text-xs text-gray-500">{d.phone || d.telephone || ''}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">Local</Badge>
                  </div>
                ));
              })()}
            </TabsContent>

            {/* Truck DB drivers */}
            <TabsContent value="truckdb" className="space-y-2 max-h-[350px] overflow-y-auto">
              {(() => {
                if (loadingTruckDb) return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
                const q = driverSearch.toLowerCase().trim();
                const filtered = truckDbDrivers.filter(d => {
                  const name = (d.nom || d.name || `${d.prenom || ''} ${d.nom_famille || ''}`).toLowerCase();
                  const phone = (d.telephone || d.phone || '').toLowerCase();
                  return !q || name.includes(q) || phone.includes(q);
                });
                if (filtered.length === 0 && q) return (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-gray-500">Aucun chauffeur Truck DB pour "{driverSearch}"</p>
                  </div>
                );
                if (filtered.length === 0) return <p className="text-center text-gray-500 py-4 text-sm">Truck DB non disponible ou aucun chauffeur</p>;
                return filtered.map((d, idx) => (
                  <div key={d.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setDriverForm({ driverId: `truckdb-${d.id || idx}`, name: d.nom || d.name || `${d.prenom || ''} ${d.nom_famille || ''}`.trim(), phone: d.telephone || d.phone || '', assignedFrom: new Date().toISOString().split('T')[0] });
                      setResourceTab('manual');
                      setDriverSearch('');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-blue-400" />
                      <div>
                        <p className="font-medium text-sm">{d.nom || d.name || `${d.prenom || ''} ${d.nom_famille || ''}`.trim()}</p>
                        <p className="text-xs text-gray-500">{d.telephone || d.phone || ''} {d.disponible ? '• Disponible' : ''}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">Truck DB</Badge>
                  </div>
                ));
              })()}
            </TabsContent>

            {/* Manual entry */}
            <TabsContent value="manual" className="space-y-4">
              <div>
                <Label>Nom complet *</Label>
                <Input
                  value={driverForm.name}
                  onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                  placeholder="Kouassi Jean"
                  data-testid="driver-name-input"
                />
              </div>
              
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={driverForm.phone}
                  onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                  placeholder="+225 07 XX XX XX"
                  data-testid="driver-phone-input"
                />
              </div>
              
              <div>
                <Label>Assigné depuis *</Label>
                <Input
                  type="date"
                  value={driverForm.assignedFrom}
                  onChange={(e) => setDriverForm({ ...driverForm, assignedFrom: e.target.value })}
                  data-testid="driver-date-input"
                />
              </div>
              {driverForm.driverId && (
                <p className="text-xs text-[#1db584]">Chauffeur pré-rempli depuis la sélection</p>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDriverModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddDriver} disabled={savingResource} className="bg-[#1db584] hover:bg-[#19a074]" data-testid="add-driver-submit">
              {savingResource ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      <Dialog open={showInvoiceDetailModal} onOpenChange={setShowInvoiceDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-3">
                  {selectedInvoice?.invoiceNumber || 'Nouvelle Facture'}
                  {selectedInvoice && <InvoiceStatusBadge status={selectedInvoice.status} />}
                </DialogTitle>
                <DialogDescription>
                  Période: {formatDate(selectedInvoice?.billingPeriod?.start)} → {formatDate(selectedInvoice?.billingPeriod?.end)}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* ✨ Bandeau Version — visible pour V2+ */}
              {(selectedInvoice.version || 1) > 1 && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50"
                  data-testid="invoice-version-banner"
                >
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-orange-900">
                      Version {selectedInvoice.version} · Remplace la version précédente
                    </div>
                    {selectedInvoice.previousSentAt && (
                      <div className="text-orange-800">
                        Édition initiale envoyée le {formatDate(selectedInvoice.previousSentAt)}
                      </div>
                    )}
                    {selectedInvoice.reopenReason && (
                      <div className="text-orange-700 mt-1 italic">
                        Motif : « {selectedInvoice.reopenReason} »
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{terminologyLabel}</p>
                  <p className="text-lg font-bold">{selectedInvoice.runsCount}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Sous-total HT</p>
                  <p className="text-lg font-bold">{formatPrice(selectedInvoice.subtotalMain)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Extras HT</p>
                  <p className="text-lg font-bold">{formatPrice(selectedInvoice.subtotalExtras)}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">Total TTC</p>
                  <p className="text-xl font-bold text-blue-700">{formatPrice(selectedInvoice.montantTotalTTC)}</p>
                </div>
              </div>

              {/* Invoice Items */}
              <div>
                <h4 className="font-medium mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Lignes principales ({selectedInvoice.invoiceItems?.length || 0})
                    {selectedInvoice.isCustomAmount && (
                      <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] ml-1">
                        Montant ajusté
                      </Badge>
                    )}
                  </span>
                  {selectedInvoice.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAddInvoiceItem}
                      data-testid="add-invoice-item-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Ajouter une ligne
                    </Button>
                  )}
                </h4>
                {selectedInvoice.invoiceItems?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Désignation</th>
                          <th className="text-center p-2 w-[140px]">Qté</th>
                          <th className="text-right p-2 w-[140px]">P.U. HT</th>
                          <th className="text-right p-2 w-[140px]">Total HT</th>
                          {selectedInvoice.status === 'draft' && <th className="w-10"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.invoiceItems.map((item) => (
                          <InvoiceItemRow
                            key={item.id}
                            item={item}
                            editable={selectedInvoice.status === 'draft'}
                            autoEdit={autoEditItemId === item.id}
                            onAutoEditConsumed={() => setAutoEditItemId(null)}
                            onUpdate={(patch) => handleUpdateInvoiceItem(item.id, patch)}
                            onRemove={() => handleRemoveInvoiceItem(item.id)}
                            formatPrice={formatPrice}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucune ligne principale</p>
                )}
              </div>

              {/* Extra Items */}
              <div>
                <h4 className="font-medium mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Extras ({selectedInvoice.extraItems?.length || 0})
                  </span>
                  {selectedInvoice.status === 'draft' && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddImmoModal(true)}
                        data-testid="add-immo-btn"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Frais d'immobilisation
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAddExtraModal(true)}
                        data-testid="add-extra-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Ajouter
                      </Button>
                    </div>
                  )}
                </h4>
                {selectedInvoice.extraItems?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="text-left p-2">Désignation</th>
                          <th className="text-center p-2">Qté</th>
                          <th className="text-right p-2">P.U. HT</th>
                          <th className="text-right p-2">Total HT</th>
                          {selectedInvoice.status === 'draft' && <th className="w-10"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.extraItems.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">
                              <p className="font-medium">{item.designation}</p>
                              {item.commentaire && <p className="text-xs text-gray-500">{item.commentaire}</p>}
                            </td>
                            <td className="text-center p-2">{item.quantite} {item.unite_payante}</td>
                            <td className="text-right p-2">{formatPrice(item.prix_unitaire_ht)}</td>
                            <td className="text-right p-2 font-medium">{formatPrice(item.montant_total_ht)}</td>
                            {selectedInvoice.status === 'draft' && (
                              <td className="p-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleRemoveExtra(item.id)}
                                  className="text-red-600 h-6 w-6 p-0"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucun extra</p>
                )}
              </div>

              {/* Paramètres de taxation — éditables en DRAFT, sinon récap */}
              {selectedInvoice.status === 'draft' ? (
                <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4" data-testid="invoice-tax-panel">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <h4 className="text-sm font-semibold text-gray-800">Paramètres de taxation</h4>
                  </div>

                  {/* TVA — grisée si le régime d'imposition interdit la TVA */}
                  <div className={`flex items-center justify-between p-3 rounded-lg border ${vatBlocked ? 'bg-gray-100 border-gray-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="invoice-tva-block">
                    <div className="flex-1 pr-3">
                      <Label className={`text-sm font-semibold ${vatBlocked ? 'text-gray-500' : 'text-emerald-900'}`}>
                        Appliquer la TVA
                        {vatBlocked && <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">indisponible</span>}
                      </Label>
                      <p className={`text-xs mt-1 ${vatBlocked ? 'text-gray-500' : 'text-emerald-700'}`}>
                        {vatBlocked ? billingPolicy.message : 'Ligne TVA visible sur la facture et la certification FNE.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="tva-on-btn"
                        disabled={vatBlocked}
                        onClick={() => handleUpdateInvoiceTax({ applyTVA: true })}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          vatBlocked
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : selectedInvoice.applyTVA
                              ? 'bg-emerald-600 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        data-testid="tva-off-btn"
                        onClick={() => handleUpdateInvoiceTax({ applyTVA: false })}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          !selectedInvoice.applyTVA
                            ? 'bg-slate-700 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>

                  {/* Taux TVA (only if TVA is applied) */}
                  {selectedInvoice.applyTVA && (
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-gray-700 min-w-[100px]">Taux TVA (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className="w-24"
                        defaultValue={selectedInvoice.tauxTVA ?? 18}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v !== (selectedInvoice.tauxTVA ?? 18)) {
                            handleUpdateInvoiceTax({ tauxTVA: v });
                          }
                        }}
                        data-testid="taux-tva-input"
                      />
                      <span className="text-sm text-gray-500">
                        → TVA : <strong>{formatPrice(selectedInvoice.montantTVA)}</strong>
                      </span>
                    </div>
                  )}

                  {/* Taxe globale cachée 2% (invisible au client) */}
                  <div className="flex items-center justify-between p-3 bg-violet-50 border border-violet-200 rounded-lg" data-testid="hidden-tax-panel">
                    <div className="flex-1 pr-3">
                      <Label className="text-sm font-semibold text-violet-900">
                        Taxe globale cachée (2%)
                      </Label>
                      <p className="text-xs text-violet-700 mt-1">
                        Incorporée dans les prix unitaires (arrondis au 100 FCFA).
                        <strong> Invisible pour le client</strong> — aucune ligne séparée.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="invoice-hidden-tax-on-btn"
                        onClick={() => handleUpdateInvoiceTax({ applyHiddenTax: true })}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          selectedInvoice.applyHiddenTax
                            ? 'bg-violet-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        data-testid="invoice-hidden-tax-off-btn"
                        onClick={() => handleUpdateInvoiceTax({ applyHiddenTax: false })}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          !selectedInvoice.applyHiddenTax
                            ? 'bg-slate-700 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  {selectedInvoice.applyHiddenTax && selectedInvoice.hiddenTax?.applied && (
                    <div className="p-2 bg-violet-50/60 border border-violet-200 rounded text-xs text-violet-800 flex justify-between">
                      <span>Base réelle HT (hors taxe cachée)</span>
                      <span className="font-medium">
                        {formatPrice(selectedInvoice.hiddenTax.baseTotalHT)} 
                        <span className="text-violet-600 ml-2">
                          (+{formatPrice(selectedInvoice.hiddenTax.taxTotal)})
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Récap total */}
                  <div className="pt-3 border-t border-gray-200 space-y-1 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Total HT</span>
                      <span>{formatPrice(selectedInvoice.montantTotalHT)}</span>
                    </div>
                    {selectedInvoice.applyTVA && (
                      <div className="flex justify-between text-gray-600">
                        <span>TVA ({selectedInvoice.tauxTVA}%)</span>
                        <span>{formatPrice(selectedInvoice.montantTVA)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-100">
                      <span>Total TTC</span>
                      <span className="text-blue-700">{formatPrice(selectedInvoice.montantTotalTTC)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                selectedInvoice.applyTVA && (
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Total HT</span>
                        <span>{formatPrice(selectedInvoice.montantTotalHT)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>TVA ({selectedInvoice.tauxTVA}%)</span>
                        <span>{formatPrice(selectedInvoice.montantTVA)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total TTC</span>
                        <span>{formatPrice(selectedInvoice.montantTotalTTC)}</span>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Payments Section */}
              {selectedInvoice.payments?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Paiements ({selectedInvoice.payments.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{formatPrice(payment.amount)}</p>
                          <p className="text-xs text-gray-500">{formatDate(payment.date)} - {payment.method}</p>
                        </div>
                        {payment.reference && <span className="text-sm text-gray-500">Réf: {payment.reference}</span>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-right text-sm">
                    <span className="text-gray-500">Reste à payer: </span>
                    <span className="font-bold text-amber-600">{formatPrice(selectedInvoice.amountDue)}</span>
                  </div>
                </div>
              )}

              {/* Payment Form (inline) */}
              {['sent', 'partially_paid', 'overdue'].includes(selectedInvoice.status) && showPaymentForm && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3" data-testid="payment-form">
                  <h4 className="font-medium flex items-center gap-2 text-emerald-800">
                    <CreditCard className="w-4 h-4" />
                    Enregistrer un paiement
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Montant (FCFA) *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        placeholder={selectedInvoice.amountDue?.toString() || '0'}
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Méthode *</Label>
                      <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                        <SelectTrigger data-testid="payment-method-select">
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="virement">Virement</SelectItem>
                          <SelectItem value="especes">Espèces</SelectItem>
                          <SelectItem value="cheque">Chèque</SelectItem>
                          <SelectItem value="mobile_money">Mobile Money</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Référence</Label>
                    <Input
                      value={paymentForm.reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                      placeholder="Numéro de transaction, chèque..."
                      data-testid="payment-reference-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => { setShowPaymentForm(false); setPaymentForm({ amount: '', method: '', reference: '', notes: '' }); }}>
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleRecordPayment}
                      disabled={savingPayment || !paymentForm.amount || !paymentForm.method}
                      data-testid="confirm-payment-btn"
                    >
                      {savingPayment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                      Confirmer
                    </Button>
                  </div>
                </div>
              )}

              {/* Status Actions Bar */}
              <div className="p-4 bg-gray-50 border rounded-lg space-y-3" data-testid="invoice-status-actions">
                <h4 className="text-sm font-medium text-gray-700">Actions sur le statut</h4>
                <div className="flex flex-wrap gap-2">
                  {/* ✨ Modifier une facture envoyée (23/04/2026 — Mode 1) */}
                  {['sent', 'overdue'].includes(selectedInvoice.status) && (selectedInvoice.amountPaid || 0) === 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => { setReopenReason(''); setShowReopenModal(true); }}
                      data-testid="reopen-invoice-btn"
                      title="Rouvrir pour édition (génère la Version N+1)"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Modifier cette facture
                    </Button>
                  )}

                  {/* 🔓 Retour brouillon — permission contract_invoices.reopen (admin), bloqué si FNE certifiée */}
                  {hasPermission('contract_invoices.reopen')
                    && ['sent', 'paid', 'partially_paid', 'overdue'].includes(selectedInvoice.status)
                    && !['certified', 'pending_fne', 'refunded', 'refunded_partial', 'refunded_total'].includes(selectedInvoice.fneCertification?.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-400 text-slate-700 hover:bg-slate-100"
                      onClick={() => { setRevertDraftReason(''); setShowRevertDraftModal(true); }}
                      data-testid="revert-to-draft-btn"
                      title="Déverrouillage admin — repasse la facture en brouillon (paiements conservés en voided)"
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Repasser en brouillon (Admin)
                    </Button>
                  )}

                  {/* ✨ Renvoyer au client après modification (V2+) */}
                  {selectedInvoice.status === 'draft' && (selectedInvoice.version || 1) > 1 && (
                    <Button
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={() => { setResendNotify(true); setShowResendModal(true); }}
                      data-testid="resend-invoice-btn"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Enregistrer et renvoyer au client
                    </Button>
                  )}

                  {/* Record Payment button */}
                  {['sent', 'partially_paid', 'overdue'].includes(selectedInvoice.status) && !showPaymentForm && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        setPaymentForm({ amount: selectedInvoice.amountDue?.toString() || '', method: '', reference: '', notes: '' });
                        setShowPaymentForm(true);
                      }}
                      data-testid="open-payment-form-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Enregistrer un paiement
                    </Button>
                  )}

                  {/* Status transition buttons */}
                  {getInvoiceStatusTransitions(selectedInvoice.status).map((transition) => {
                    const Icon = transition.icon;
                    return (
                      <Button
                        key={transition.value}
                        size="sm"
                        className={transition.color + ' text-white'}
                        onClick={() => {
                          setInvoiceNewStatus(transition.value);
                          setShowInvoiceStatusModal(true);
                        }}
                        data-testid={`invoice-status-${transition.value}-btn`}
                      >
                        <Icon className="w-4 h-4 mr-1" />
                        {transition.label}
                      </Button>
                    );
                  })}

                  {/* Delete draft */}
                  {selectedInvoice.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200"
                      onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                  )}

                  {/* Download PDF */}
                  {['validated', 'sent', 'paid', 'partially_paid', 'overdue'].includes(selectedInvoice.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadInvoicePDF(selectedInvoice.id, selectedInvoice.invoiceNumber, selectedInvoice.version)}
                      data-testid="download-invoice-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  )}

                  {/* ✨ Historique / Versions */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowHistoryModal(true)}
                    data-testid="invoice-history-btn"
                    title="Voir l'historique d'audit et les versions PDF archivées"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Historique{(selectedInvoice.version || 1) > 1 && (
                      <span className="ml-1 text-[10px] px-1.5 rounded bg-orange-100 text-orange-700">
                        V{selectedInvoice.version}
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {/* FNE Certification (DGI) — factures contrat */}
              {(['validated', 'sent', 'paid', 'partially_paid', 'overdue'].includes(selectedInvoice.status) || selectedInvoice.fneCertification) && (
                <div className="p-4 bg-emerald-50/40 border border-emerald-200 rounded-lg space-y-2" data-testid="invoice-fne-section">
                  <h4 className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Certification FNE (DGI)
                  </h4>
                  <FneCertifyButton
                    certification={selectedInvoice.fneCertification}
                    targetType="contract"
                    targetId={selectedInvoice.id}
                    targetLabel={selectedInvoice.invoiceNumber}
                    onCertified={() => { openInvoiceDetail(selectedInvoice); fetchInvoices(); }}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInvoiceDetailModal(false); setShowPaymentForm(false); }}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FNE Certify modal (depuis le menu d'actions facture) */}
      {fneCertifyInvoice && (
        <FneCertifyModal
          open={!!fneCertifyInvoice}
          onClose={() => setFneCertifyInvoice(null)}
          onSuccess={() => { setFneCertifyInvoice(null); fetchInvoices(); }}
          targetType="contract"
          targetId={fneCertifyInvoice.id}
          targetLabel={fneCertifyInvoice.invoiceNumber}
        />
      )}

      {/* FNE Refund (Avoir) modal */}
      {fneRefundInvoice && (
        <FneRefundModal
          open={!!fneRefundInvoice}
          onClose={() => setFneRefundInvoice(null)}
          onSuccess={() => { setFneRefundInvoice(null); fetchInvoices(); }}
          targetType="contract"
          targetId={fneRefundInvoice.id}
          targetLabel={fneRefundInvoice.invoiceNumber}
        />
      )}

      {/* Add Extra Modal */}
      {/* PM Spec 17/02/2026 — Modale : Frais d'immobilisation structurés */}
      <Dialog open={showAddImmoModal} onOpenChange={setShowAddImmoModal}>
        <DialogContent data-testid="immo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Ajouter des frais d'immobilisation
            </DialogTitle>
            <DialogDescription>
              Calculé automatiquement depuis la durée, le tarif convenu et la franchise.
              Sera ajouté en ligne extra sur la facture en cours.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durée effective *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={immoForm.duration}
                  onChange={(e) => setImmoForm({ ...immoForm, duration: e.target.value, amount_override: '' })}
                  data-testid="immo-duration-input"
                />
              </div>
              <div>
                <Label>Unité</Label>
                <Select
                  value={immoForm.unit}
                  onValueChange={(value) => setImmoForm({ ...immoForm, unit: value, amount_override: '' })}
                >
                  <SelectTrigger data-testid="immo-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="heures">Heures</SelectItem>
                    <SelectItem value="jours">Jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarif convenu (XOF/jour) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={immoForm.rate_per_day}
                  onChange={(e) => setImmoForm({ ...immoForm, rate_per_day: e.target.value, amount_override: '' })}
                  data-testid="immo-rate-input"
                />
              </div>
              <div>
                <Label>Franchise ({immoForm.unit})</Label>
                <Input
                  type="number"
                  min="0"
                  value={immoForm.franchise}
                  onChange={(e) => setImmoForm({ ...immoForm, franchise: e.target.value, amount_override: '' })}
                  data-testid="immo-franchise-input"
                />
              </div>
            </div>

            {parseFloat(immoForm.duration) > 0 && (
              <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">Quantité facturable :</span>
                  <span className="font-mono">
                    max(0, {parseFloat(immoForm.duration)} − {parseFloat(immoForm.franchise) || 0}) = <strong>{immoPreview.billable} {immoForm.unit}</strong>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Montant calculé :</span>
                  <span className="font-mono font-semibold text-amber-700">
                    {immoPreview.auto.toLocaleString('fr-FR')} XOF
                  </span>
                </div>
                {immoPreview.overridden && (
                  <div className="flex justify-between text-amber-800">
                    <span>Montant final (ajusté) :</span>
                    <span className="font-mono font-bold">{immoPreview.final.toLocaleString('fr-FR')} XOF</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={immoForm.amount_override !== ''}
                  onChange={(e) => setImmoForm({ ...immoForm, amount_override: e.target.checked ? immoPreview.auto : '' })}
                  data-testid="immo-override-checkbox"
                />
                Ajuster manuellement le montant
              </Label>
              {immoForm.amount_override !== '' && (
                <Input
                  type="number"
                  min="0"
                  value={immoForm.amount_override}
                  onChange={(e) => setImmoForm({ ...immoForm, amount_override: e.target.value })}
                  className="mt-1 text-right font-mono"
                  data-testid="immo-override-input"
                />
              )}
            </div>

            <div>
              <Label>Note (optionnel)</Label>
              <Textarea
                value={immoForm.note}
                onChange={(e) => setImmoForm({ ...immoForm, note: e.target.value })}
                placeholder="Ex: Immobilisation chantier Bouaké du 12 au 15/02"
                rows={2}
                data-testid="immo-note-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddImmoModal(false)} disabled={savingImmo}>
              Annuler
            </Button>
            <Button
              onClick={handleAddImmobilization}
              disabled={savingImmo || !immoForm.duration || immoPreview.final <= 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="immo-submit-btn"
            >
              {savingImmo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              Ajouter à la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showAddExtraModal} onOpenChange={setShowAddExtraModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un Extra</DialogTitle>
            <DialogDescription>
              Ajoutez des frais supplémentaires à cette facture (carburant, attente, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Désignation *</Label>
              <Input
                value={extraForm.designation}
                onChange={(e) => setExtraForm({ ...extraForm, designation: e.target.value })}
                placeholder="Ex: Frais de carburant, Temps d'attente..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unité</Label>
                <Select 
                  value={extraForm.unite_payante}
                  onValueChange={(value) => setExtraForm({ ...extraForm, unite_payante: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Forfait">Forfait</SelectItem>
                    <SelectItem value="Litre">Litre</SelectItem>
                    <SelectItem value="Heure">Heure</SelectItem>
                    <SelectItem value="Jour">Jour</SelectItem>
                    <SelectItem value="Km">Km</SelectItem>
                    <SelectItem value="Pesée">Pesée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Quantité</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={extraForm.quantite}
                  onChange={(e) => setExtraForm({ ...extraForm, quantite: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Prix unitaire HT (FCFA) *</Label>
              <Input
                type="number"
                min="0"
                value={extraForm.prix_unitaire_ht}
                onChange={(e) => setExtraForm({ ...extraForm, prix_unitaire_ht: e.target.value })}
                placeholder="0"
              />
            </div>
            
            <div>
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={extraForm.commentaire}
                onChange={(e) => setExtraForm({ ...extraForm, commentaire: e.target.value })}
                placeholder="Détails supplémentaires..."
                rows={2}
              />
            </div>
            
            {extraForm.quantite && extraForm.prix_unitaire_ht && (
              <div className="p-3 bg-blue-50 rounded-lg text-right">
                <span className="text-sm text-gray-600">Total HT: </span>
                <span className="font-bold text-blue-700">
                  {formatPrice(parseFloat(extraForm.quantite) * parseFloat(extraForm.prix_unitaire_ht))}
                </span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExtraModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddExtra} disabled={savingExtra}>
              {savingExtra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✨ MODAL — Rouvrir une facture envoyée pour édition (V N+1) */}
      <Dialog open={showReopenModal} onOpenChange={setShowReopenModal}>
        <DialogContent data-testid="reopen-invoice-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Modifier la facture {selectedInvoice?.invoiceNumber}
            </DialogTitle>
            <DialogDescription>
              Cette facture a déjà été envoyée au client. La modifier va générer une nouvelle
              version (V{(selectedInvoice?.version || 1) + 1}) qui annule et remplace la version actuelle.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 rounded-lg border border-orange-200 bg-orange-50 text-sm text-orange-900">
              <p className="font-medium mb-1">📌 Avant de continuer :</p>
              <ul className="list-disc list-inside space-y-0.5 text-orange-800">
                <li>L'ancienne version reste auditable dans l'historique</li>
                <li>Vous pourrez modifier les lignes, quantités et tarifs</li>
                <li>Un nouveau PDF sera généré et (optionnellement) renvoyé au client</li>
              </ul>
            </div>

            <div>
              <Label htmlFor="reopen-reason" className="text-sm font-medium">
                Motif de modification <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reopen-reason"
                placeholder="Ex: Erreur de quantité ligne 2 — client a remonté une facturation en trop sur le camion #1..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="reopen-reason-input"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">
                  Min. 10 caractères — tracé dans l'historique d'audit
                </span>
                <span className={`text-xs ${reopenReason.trim().length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>
                  {reopenReason.trim().length} / 10
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReopenModal(false)} disabled={reopening}>
              Annuler
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleReopenInvoice}
              disabled={reopening || reopenReason.trim().length < 10}
              data-testid="confirm-reopen-btn"
            >
              {reopening ? 'Réouverture...' : 'Confirmer et rouvrir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🔓 MODAL — Retour brouillon (Admin) */}
      <Dialog open={showRevertDraftModal} onOpenChange={setShowRevertDraftModal}>
        <DialogContent data-testid="revert-to-draft-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-slate-600" />
              Repasser en brouillon — {selectedInvoice?.invoiceNumber}
            </DialogTitle>
            <DialogDescription>
              Déverrouillage administrateur exceptionnel. La facture repassera en <strong>brouillon</strong> et sera éditable.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 rounded-lg border border-slate-300 bg-slate-50 text-sm text-slate-800">
              <p className="font-medium mb-1">📌 À savoir avant de confirmer :</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                <li>Statut actuel : <strong>{selectedInvoice?.status}</strong> → passe en <strong>brouillon</strong></li>
                {(selectedInvoice?.amountPaid || 0) > 0 && (
                  <li className="text-amber-800">
                    Un paiement de <strong>{(selectedInvoice?.amountPaid || 0).toLocaleString('fr-FR')} FCFA</strong> sera marqué comme annulé (voided) mais conservé pour l'audit
                  </li>
                )}
                <li>Action <strong>bloquée</strong> si la facture est certifiée FNE ou si un mouvement bancaire est rapproché</li>
                <li>Toute l'opération est tracée dans le log d'audit (motif inclus)</li>
              </ul>
            </div>

            <div>
              <Label htmlFor="revert-draft-reason" className="text-sm font-medium">
                Motif du déverrouillage <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="revert-draft-reason"
                placeholder="Ex: Erreur de saisie sur la période de facturation — correction demandée par le client avant paiement effectif, non certifiée FNE."
                value={revertDraftReason}
                onChange={(e) => setRevertDraftReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="revert-draft-reason-input"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">
                  Min. 20 caractères — obligatoire, tracé dans le log d'audit
                </span>
                <span className={`text-xs ${revertDraftReason.trim().length >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                  {revertDraftReason.trim().length} / 20
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevertDraftModal(false)} disabled={revertingDraft}>
              Annuler
            </Button>
            <Button
              className="bg-slate-700 hover:bg-slate-800 text-white"
              onClick={handleRevertToDraft}
              disabled={revertingDraft || revertDraftReason.trim().length < 20}
              data-testid="confirm-revert-draft-btn"
            >
              {revertingDraft ? 'Traitement...' : 'Confirmer le retour en brouillon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✨ MODAL — Renvoyer une facture après modification */}
      <Dialog open={showResendModal} onOpenChange={setShowResendModal}>
        <DialogContent data-testid="resend-invoice-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-orange-600" />
              Renvoyer la facture V{selectedInvoice?.version || 1}
            </DialogTitle>
            <DialogDescription>
              {selectedInvoice?.invoiceNumber} — un nouveau PDF sera généré avec les modifications.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-50">
              <input
                type="checkbox"
                id="resend-notify"
                checked={resendNotify}
                onChange={(e) => setResendNotify(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-orange-600"
                data-testid="resend-notify-checkbox"
              />
              <Label htmlFor="resend-notify" className="text-sm cursor-pointer select-none">
                <span className="font-medium">Notifier le client par email</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Envoie automatiquement le nouveau PDF au client avec mention « Version corrigée ».
                  Décochez si vous préférez communiquer le changement autrement (téléphone, messagerie…).
                </span>
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResendModal(false)} disabled={resending}>
              Annuler
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleResendInvoice}
              disabled={resending}
              data-testid="confirm-resend-btn"
            >
              {resending ? 'Envoi...' : (resendNotify ? 'Renvoyer au client' : 'Marquer comme envoyée')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✨ MODAL — Historique des versions */}
      <InvoiceHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        invoice={selectedInvoice}
        onToast={toast}
      />

      {/* Invoice Status Change Confirmation Modal */}
      <Dialog open={showInvoiceStatusModal} onOpenChange={setShowInvoiceStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le statut de la facture</DialogTitle>
            <DialogDescription>
              {selectedInvoice?.invoiceNumber} — {INVOICE_STATUS_CONFIG[selectedInvoice?.status]?.label} → {INVOICE_STATUS_CONFIG[invoiceNewStatus]?.label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <InvoiceStatusBadge status={selectedInvoice?.status} />
              <span className="text-gray-400">→</span>
              <InvoiceStatusBadge status={invoiceNewStatus} />
            </div>

            {invoiceNewStatus === 'paid' && selectedInvoice?.amountDue > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Reste à payer : <strong>{formatPrice(selectedInvoice.amountDue)}</strong>. Ce montant sera automatiquement soldé.
              </div>
            )}

            <div>
              <Label className="text-sm">Motif (optionnel)</Label>
              <Textarea
                value={invoiceStatusReason}
                onChange={(e) => setInvoiceStatusReason(e.target.value)}
                placeholder="Raison du changement de statut..."
                rows={2}
                data-testid="invoice-status-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInvoiceStatusModal(false); setInvoiceNewStatus(''); setInvoiceStatusReason(''); }}>
              Annuler
            </Button>
            <Button
              onClick={handleChangeInvoiceStatus}
              disabled={changingInvoiceStatus}
              data-testid="confirm-invoice-status-btn"
            >
              {changingInvoiceStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POD Modal */}
      <Dialog open={showPODModal} onOpenChange={setShowPODModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preuve de Livraison (POD)</DialogTitle>
            <DialogDescription>
              {selectedStop?.address} • {selectedRun?.runNumber}
            </DialogDescription>
          </DialogHeader>
          
          {selectedStop?.pod && (
            <div className="space-y-4">
              {/* Signature */}
              {selectedStop.pod.signatureUrl && (
                <div>
                  <h4 className="font-medium mb-2">Signature</h4>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img 
                      src={selectedStop.pod.signatureUrl} 
                      alt="Signature" 
                      className="max-h-32 mx-auto"
                    />
                    <p className="text-center text-sm text-gray-500 mt-2">
                      Signé par: {selectedStop.pod.signedBy || 'Non spécifié'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Photos */}
              {selectedStop.pod.photos?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Photos ({selectedStop.pod.photos.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedStop.pod.photos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={photo.url} 
                          alt={photo.caption || `Photo ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        {photo.caption && (
                          <p className="text-xs text-gray-500 mt-1 truncate">{photo.caption}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Documents */}
              {selectedStop.pod.documents?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Documents ({selectedStop.pod.documents.length})</h4>
                  <div className="space-y-2">
                    {selectedStop.pod.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{doc.type}</span>
                          {doc.number && <span className="text-xs text-gray-500">#{doc.number}</span>}
                        </div>
                        <Button size="sm" variant="ghost" asChild>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedStop.pod.notes && (
                <div>
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {selectedStop.pod.notes}
                  </p>
                </div>
              )}
              
              {/* Validation Status */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Statut de validation</p>
                    {getPODStatusBadge(selectedStop.pod)}
                    {selectedStop.pod.rejectionReason && (
                      <p className="text-sm text-red-600 mt-1">
                        Raison: {selectedStop.pod.rejectionReason}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Capturé le {formatDate(selectedStop.pod.capturedAt)}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedStop?.pod?.validationStatus === 'pending' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const reason = window.prompt("Raison du rejet :");
                    if (reason) handleValidatePOD(selectedRun.id, selectedStop.id, false, reason);
                  }}
                  disabled={validatingPOD}
                  className="text-red-600"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button 
                  onClick={() => handleValidatePOD(selectedRun.id, selectedStop.id, true)}
                  disabled={validatingPOD}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {validatingPOD ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Valider
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowPODModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de supprimer {getDeletableSelectedRuns().length} {terminologyLabel.toLowerCase()}.
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="max-h-48 overflow-y-auto space-y-2">
              {getDeletableSelectedRuns().map(run => (
                <div key={run.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <span className="font-mono">{run.runNumber || `#${run.id.slice(0,8)}`}</span>
                  <span className="text-gray-500">{formatDate(run.date)}</span>
                </div>
              ))}
            </div>
            {selectedRunIds.length !== getDeletableSelectedRuns().length && (
              <p className="mt-3 text-sm text-amber-600">
                Note: {selectedRunIds.length - getDeletableSelectedRuns().length} élément(s) ne peuvent pas être supprimé(s) car en cours ou terminé(s).
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={bulkDeleting}
            >
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting || getDeletableSelectedRuns().length === 0}
            >
              {bulkDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Supprimer ({getDeletableSelectedRuns().length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Associate Modal */}
      <Dialog open={showBulkAssociateModal} onOpenChange={setShowBulkAssociateModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700" data-testid="associate-modal-title">
              <Users className="w-5 h-5" />
              Associer à une opération mère
            </DialogTitle>
            <DialogDescription>
              Sélectionnez l'opération mère à laquelle associer les {selectedRunIds.length} transfert(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col gap-4 py-2">
            {/* Transferts sélectionnés - compact */}
            <div className="flex-shrink-0">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                Transferts sélectionnés ({selectedRunIds.length})
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {runs.filter(r => selectedRunIds.includes(r.id)).map(run => (
                  <Badge key={run.id} variant="secondary" className="text-xs font-mono px-2 py-1 bg-gray-100">
                    {run.runNumber ? run.runNumber.split('-').slice(-2).join('-') : `#${run.id.slice(0,6)}`}
                    <span className="ml-1.5 text-gray-400">{formatDate(run.date)}</span>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Séparateur */}
            <div className="border-t flex-shrink-0" />

            {/* Recherche d'opération */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block flex-shrink-0">
                Opération mère cible
              </Label>
              <div className="relative mb-2 flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher par numéro, trajet, client..."
                  value={associateSearchQuery}
                  onChange={(e) => setAssociateSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="associate-search-input"
                />
              </div>
              
              {loadingAllParentOps ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : filteredAssociateOperations.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Aucune opération trouvée
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto border rounded-lg min-h-0" data-testid="associate-operations-list">
                  {filteredAssociateOperations.map((op, idx) => (
                    <div
                      key={op.id}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 cursor-pointer transition-all",
                        idx !== 0 && "border-t",
                        associateTargetOperationId === op.id 
                          ? "bg-blue-50 border-l-[3px] border-l-blue-600" 
                          : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                      )}
                      onClick={() => setAssociateTargetOperationId(op.id)}
                      data-testid={`associate-op-${op.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn(
                            "font-mono text-sm font-semibold",
                            associateTargetOperationId === op.id ? "text-blue-700" : "text-gray-800"
                          )}>
                            {op.operationNumber || `#${op.id.slice(0,8)}`}
                          </span>
                          {op.isParentOperation && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0">Parent</Badge>
                          )}
                          {op.childrenCount > 0 && (
                            <span className="text-[10px] text-gray-400">{op.childrenCount} enfant(s)</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5 truncate">
                          {op.departure || 'Départ'} → {op.arrival || 'Arrivée'}
                        </p>
                        {(op.firstName || op.lastName) && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {op.firstName} {op.lastName}
                          </p>
                        )}
                      </div>
                      {associateTargetOperationId === op.id && (
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-2 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBulkAssociateModal(false);
                setAssociateTargetOperationId('');
                setAssociateSearchQuery('');
              }}
              disabled={bulkAssociating}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleBulkAssociate}
              disabled={bulkAssociating || !associateTargetOperationId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-associate-btn"
            >
              {bulkAssociating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Users className="w-4 h-4 mr-2" />
              )}
              Associer ({selectedRunIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale assignation vehicule/chauffeur en masse */}
      <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-600" />
              Assigner Véhicule / Chauffeur
            </DialogTitle>
            <DialogDescription>
              Assigner un vehicule et/ou un chauffeur a {selectedRunIds.length} transfert(s) selectionne(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Vehicle search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Véhicule</Label>
              {bulkAssignVehicle ? (
                <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-emerald-50 border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-mono font-semibold text-emerald-800">{bulkAssignVehicle.label}</p>
                      <p className="text-xs text-emerald-600">
                        {[bulkAssignVehicle.brand, bulkAssignVehicle.vehicleType, bulkAssignVehicle.capacity, bulkAssignVehicle.partnerName].filter(Boolean).join(' - ')}
                      </p>
                    </div>
                    <Badge className={`text-[10px] shrink-0 ${bulkAssignVehicle.status === 'disponible' ? 'bg-green-100 text-green-700' : bulkAssignVehicle.status === 'occupe' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                      {bulkAssignVehicle.status === 'disponible' ? 'Dispo' : bulkAssignVehicle.status === 'occupe' ? 'Occupe' : bulkAssignVehicle.status}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setBulkAssignVehicle(null)} data-testid="clear-vehicle-btn">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center border rounded-md px-3 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500">
                    <Search className="w-4 h-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      className="w-full py-2 pl-2 text-sm outline-none bg-transparent"
                      placeholder="Tapez 2 caracteres min (ex: 2377, DAF...)"
                      value={bulkVehicleQ}
                      onChange={(e) => setBulkVehicleQ(e.target.value)}
                      data-testid="bulk-assign-vehicle-search"
                    />
                    {loadingBulkVehicles && <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />}
                  </div>
                  {bulkVehicleOpts.length > 0 && !bulkAssignVehicle && (
                    <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-md shadow-lg" data-testid="vehicle-dropdown">
                      {bulkVehicleOpts.map(v => (
                        <button
                          key={v.id}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm border-b last:border-0"
                          onClick={() => { setBulkAssignVehicle(v); setBulkVehicleQ(''); }}
                          data-testid={`vehicle-option-${v.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="font-mono font-medium truncate">{v.label}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {[v.brand, v.model, v.vehicleType, v.capacity, v.partnerName].filter(Boolean).join(' - ')}
                              </p>
                            </div>
                            <Badge className={`text-[9px] ml-2 shrink-0 ${v.status === 'disponible' ? 'bg-green-100 text-green-700' : v.status === 'occupe' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                              {v.status === 'disponible' ? 'Dispo' : v.status === 'occupe' ? 'Occupe' : v.status}
                            </Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Driver search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Chauffeur</Label>
              {bulkAssignDriver ? (
                <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-blue-50 border-blue-200">
                  <div>
                    <p className="text-sm font-semibold text-blue-800">{bulkAssignDriver.name}</p>
                    {bulkAssignDriver.phone && <p className="text-xs text-blue-600">{bulkAssignDriver.phone}</p>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setBulkAssignDriver(null)} data-testid="clear-driver-btn">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center border rounded-md px-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <Search className="w-4 h-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      className="w-full py-2 pl-2 text-sm outline-none bg-transparent"
                      placeholder="Tapez 2 caracteres min (ex: KONE, MAMADOU...)"
                      value={bulkDriverQ}
                      onChange={(e) => setBulkDriverQ(e.target.value)}
                      data-testid="bulk-assign-driver-search"
                    />
                    {loadingBulkDrivers && <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />}
                  </div>
                  {bulkDriverOpts.length > 0 && !bulkAssignDriver && (
                    <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-md shadow-lg" data-testid="driver-dropdown">
                      {bulkDriverOpts.map(d => (
                        <button
                          key={d.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex justify-between items-center border-b last:border-0"
                          onClick={() => { setBulkAssignDriver(d); setBulkDriverQ(''); }}
                          data-testid={`driver-option-${d.id}`}
                        >
                          <div>
                            <span className="font-medium">{d.name}</span>
                            {d.phone && <span className="ml-2 text-xs text-gray-400">{d.phone}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {(!bulkAssignVehicle && !bulkAssignDriver) && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Sélectionnez au moins un véhicule ou un chauffeur</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkAssignModal(false)} disabled={bulkAssigning}>
              Annuler
            </Button>
            <Button
              onClick={handleBulkAssignResources}
              disabled={bulkAssigning || (!bulkAssignVehicle && !bulkAssignDriver)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-assign-confirm-btn"
            >
              {bulkAssigning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assignation...</> : `Assigner (${selectedRunIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale changement de statut en masse — Onglet Transferts */}
      <BulkStatusChangeModal
        open={showBulkStatusModal}
        onClose={() => setShowBulkStatusModal(false)}
        runIds={selectedRunIds}
        onSuccess={() => {
          setSelectedRunIds([]);
          fetchRuns();
        }}
      />

      {/* Modale suppression du contrat */}
      <Dialog open={showDeleteContractModal} onOpenChange={setShowDeleteContractModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le contrat
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de supprimer définitivement le contrat&nbsp;
              <strong>{contract?.contractNumber}</strong> ({contract?.clientName}).
              <br /><br />
              Cette action est <strong>irréversible</strong>. Toutes les données associées (transferts, factures, documents) resteront en base mais le contrat sera supprimé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteContractModal(false)} disabled={deletingContract}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteContract}
              disabled={deletingContract}
              data-testid="confirm-delete-contract-btn"
            >
              {deletingContract
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Suppression...</>
                : <><Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Transfer Wizard */}
      <CreateTransferWizard
        isOpen={showCreateRunModal}
        onClose={() => setShowCreateRunModal(false)}
        onSuccess={() => {
          fetchRuns();
          setShowCreateRunModal(false);
        }}
        contractId={id}
        contractNumber={contract?.contractNumber}
        clientName={contract?.clientName || `${contract?.customer?.firstName} ${contract?.customer?.lastName}`}
        clientId={contract?.customerId || contract?.customer?.id}
        terminology={TERMINOLOGY_CONFIG[contract?.terminology]?.label || 'Transfert'}
        parentOperations={availableParentOperations}
      />

      {/* ─── Fleet Modification Modal ─── */}
      <Dialog open={showFleetModal} onOpenChange={setShowFleetModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Truck className="w-5 h-5" />
              Modifier la composition de flotte
            </DialogTitle>
            <DialogDescription>
              Cette modification créera un avenant daté et tracé. Les factures déjà émises ne seront pas modifiées.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Amendment meta */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <Label className="text-amber-800">Date d'effet de l'avenant *</Label>
                <Input
                  type="date"
                  value={fleetAmendmentDate}
                  onChange={e => setFleetAmendmentDate(e.target.value)}
                  className="mt-1 border-amber-200"
                />
              </div>
              <div>
                <Label className="text-amber-800">Motif de l'avenant *</Label>
                <Input
                  value={fleetAmendmentReason}
                  onChange={e => setFleetAmendmentReason(e.target.value)}
                  placeholder="Ex: Ajout 2 camions plateau — mars 2026"
                  className="mt-1 border-amber-200"
                />
              </div>
            </div>

            {/* Fleet items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Nouvelle composition de flotte</Label>
                <Button type="button" size="sm" variant="outline" onClick={addFleetItem} className="border-blue-300 text-blue-700">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter un type
                </Button>
              </div>

              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
                <div className="col-span-5">Type de camion</div>
                <div className="col-span-2 text-center">Qté</div>
                <div className="col-span-4">Tarif/camion (FCFA)</div>
                <div className="col-span-1"></div>
              </div>

              {fleetItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg p-2 border">
                  <div className="col-span-5">
                    <Select value={item.vehicleType} onValueChange={(v) => updateFleetItem(index, 'vehicleType', v)}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Choisir un type..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {VEHICLE_TYPES.filter(v => v.value && v.label).map(v => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" min="1" max="999"
                        value={item.quantity}
                        onChange={e => updateFleetItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="h-8 text-sm text-center"
                        placeholder="ex: 3"
                      />
                      <span className="text-xs text-gray-400 whitespace-nowrap">cam.</span>
                    </div>
                  </div>
                  <div className="col-span-4">
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" min="0"
                        value={item.monthlyRatePerTruck}
                        onChange={e => updateFleetItem(index, 'monthlyRatePerTruck', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm"
                        placeholder="ex: 3000000"
                      />
                      <span className="text-xs text-gray-400 whitespace-nowrap">FCFA</span>
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {fleetItems.length > 1 && (
                      <button type="button" onClick={() => removeFleetItem(index)} className="text-red-400 hover:text-red-600 p-1">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {item.vehicleType && item.monthlyRatePerTruck > 0 && (
                    <div className="col-span-12 text-right text-xs text-blue-600 font-medium pr-1">
                      Sous-total : {new Intl.NumberFormat('fr-FR').format((item.quantity || 0) * (item.monthlyRatePerTruck || 0))} FCFA/mois
                    </div>
                  )}
                </div>
              ))}

              {/* New total */}
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{fleetTotalTrucks} camion{fleetTotalTrucks > 1 ? 's' : ''}</span> mobilisé{fleetTotalTrucks > 1 ? 's' : ''}
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Nouvelle redevance mensuelle</p>
                  <p className="text-xl font-bold text-blue-800">
                    {new Intl.NumberFormat('fr-FR').format(fleetTotal)} <span className="text-sm font-normal text-gray-500">FCFA/mois</span>
                  </p>
                  {contract?.billingRules?.totalMonthlyFleetAmount && (
                    <p className="text-xs text-gray-400">
                      Précédemment : {new Intl.NumberFormat('fr-FR').format(contract.billingRules.totalMonthlyFleetAmount)} FCFA
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFleetModal(false)}>Annuler</Button>
            <Button onClick={handleSaveFleet} disabled={savingFleet} className="bg-blue-600 hover:bg-blue-700">
              {savingFleet ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer l'avenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <TransferCSVImportModal
        open={showCSVImportModal}
        onOpenChange={setShowCSVImportModal}
        contractId={contract?.id}
        contractNumber={contract?.contractNumber}
        clientName={contract?.clientName || `${contract?.customer?.firstName || ''} ${contract?.customer?.lastName || ''}`.trim()}
        companyName={contract?.customer?.companyName || contract?.companyName || ''}
        accountType={contract?.customer?.accountType || contract?.accountType || 'entreprise'}
        operations={allParentOperations}
        onSuccess={() => {
          fetchRuns();
          fetchAllParentOperations();
        }}
      />
    </div>
  );
};

export default ContractDetail;
